import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, Trash2, Plus, Minus, DollarSign, Wallet, FileText, Receipt, Smartphone, Tag, History, Phone, Scale, Printer, Package, Users, X, Percent, CreditCard, Barcode, Zap, Tv, Ticket, Pause, Play, Clock, Info, MapPin, Truck, AlertTriangle, Gift, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import CheckoutModal from "@/components/CheckoutModal";
import PettyCashModal from "@/components/PettyCashModal";
import { useCustomerLevels, CUSTOMER_LEVELS_FALLBACK } from "@/hooks/useCustomerLevels";
import PayCasualModal from "@/components/PayCasualModal";
import LoadQuotationModal from "@/components/LoadQuotationModal";
import AccountReceiptModal from "@/components/AccountReceiptModal";
import ServicesModal from "@/components/ServicesModal";
import PriceCheckModal from "@/components/PriceCheckModal";
import RedeemVoucherDialog from "@/components/RedeemVoucherDialog";
import { SellGiftVoucherDialog } from "@/components/giftvouchers/SellGiftVoucherDialog";
import { printVoucherSlip } from "@/utils/voucherPrint";
import SalesHistoryPanel from "@/components/SalesHistoryPanel";
import CustomerBalanceModal from "@/components/CustomerBalanceModal";
import { QuickButtons } from "@/components/QuickButtons";
import { LoanDialog } from "@/components/sales/LoanDialog";
import { CashWithdrawalDialog } from "@/components/sales/CashWithdrawalDialog";
import { WeightDialog } from "@/components/sales/WeightDialog";
import { DiscountDialog } from "@/components/sales/DiscountDialog";
import { FixBarcodeDialog } from "@/components/sales/FixBarcodeDialog";
import { ShelfLocationLookupModal } from "@/components/sales/ShelfLocationLookupModal";
import { TillNoticeModal } from "@/components/TillNoticeModal";
import { VasServiceDialog } from "@/components/sales/VasServiceDialog";
import { HeldSalesDialog } from "@/components/sales/HeldSalesDialog";
import SalespersonSelector from "@/components/SalespersonSelector";
import MyCommissionWidget from "@/components/MyCommissionWidget";
import ReservedStockOverrideModal from "@/components/ReservedStockOverrideModal";
import { useVat } from "@/context/VatContext";
import { useAuth } from "@/context/AuthContext";
import { useLiveMonitor } from "@/hooks/useLiveMonitor";
import { 
  initOfflineDB, 
  getCachedProducts, 
  searchCachedProducts, 
  getCachedProductByCode,
  getPendingOfflineSalesCount,
  hasCachedProducts 
} from "@/utils/offlineStorage";
import { isOnline as connectivityIsOnline, subscribeOnline } from "@/utils/connectivity";
import { DirectSaleLineDialog } from "@/components/direct/DirectSaleLineDialog";
import { PriceOverrideReasonDialog } from "@/components/sales/PriceOverrideReasonDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Sales = () => {
  const { vatEnabled, vatRate } = useVat();
  const { isAdmin, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [cart, setCart] = useState([]);
  // Reserved-stock override modal — set with payload to open, null to close.
  // Payload shape:
  //   { productName, requestedQty, availableUnreserved, reservedQty,
  //     reservedFor, reservationNote, apply: () => void }
  // `apply` is what runs once the cashier successfully overrides with a PIN.
  const [reservedOverride, setReservedOverride] = useState(null);
  const [quickAccessProducts, setQuickAccessProducts] = useState([]);
  const [priceLevel, setPriceLevel] = useState(1);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [pettyCashOpen, setPettyCashOpen] = useState(false);
  const [payCasualOpen, setPayCasualOpen] = useState(false);
  const [loadQuoteOpen, setLoadQuoteOpen] = useState(false);
  const [accountReceiptOpen, setAccountReceiptOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [priceCheckOpen, setPriceCheckOpen] = useState(false);
  const [redeemVoucherOpen, setRedeemVoucherOpen] = useState(false);
  const [sellGiftVoucherOpen, setSellGiftVoucherOpen] = useState(false);
  const [loanOpen, setLoanOpen] = useState(false);
  const [loanAmount, setLoanAmount] = useState("");
  const [loanInterestRate, setLoanInterestRate] = useState(20);
  const [loanFromTill, setLoanFromTill] = useState(null);
  const [cashWithdrawalOpen, setCashWithdrawalOpen] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [withdrawalFeeRate, setWithdrawalFeeRate] = useState(5);
  const [withdrawalFromTill, setWithdrawalFromTill] = useState(null);
  const [loadedQuotation, setLoadedQuotation] = useState(null);
  const [loadedOnlineOrder, setLoadedOnlineOrder] = useState(null);
  const [searching, setSearching] = useState(false);
  const [appliedPromotions, setAppliedPromotions] = useState([]);
  const [promotionDiscount, setPromotionDiscount] = useState(0);
  // Spend-reward status from /promotions/apply. Each entry describes
  // an active spend-reward promo, whether the CURRENT basket qualifies,
  // and how much more spend is needed. When qualified, the till auto-
  // adds a zero-value voucher line to the cart (see useEffect below).
  const [spendRewards, setSpendRewards] = useState([]);
  // Set true when the cashier picks ACCOUNT as a payment method at
  // checkout. Spend-reward vouchers are cash/card-only, so account
  // sales must not receive them. Auto-add skips while this is true,
  // and any existing voucher line is stripped out on the spot.
  const [voucherBlocked, setVoucherBlocked] = useState(false);
  // PLU codes of cart items that /promotions/apply flagged as receiving a
  // promotional discount. Used to SKIP the customer-level auto-discount
  // (loyalty %, level %, custom %) on these items so a shopper never gets
  // "promo price minus another 2%". Populated in checkPromotions().
  const [promoPluCodes, setPromoPluCodes] = useState(() => new Set());
  const [selectedCartItem, setSelectedCartItem] = useState(null);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  
  // Quantity edit state
  const [editingQtyProductId, setEditingQtyProductId] = useState(null);
  const [editingQtyValue, setEditingQtyValue] = useState("");
  const qtyInputRef = useRef(null);
  

  // Price edit state
  const [editingPriceProductId, setEditingPriceProductId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState("");
  const [pendingPriceOverride, setPendingPriceOverride] = useState(null);
  const priceInputRef = useRef(null);

  // Ref for search input
  const searchInputRef = useRef(null);
  
  // Ref to track when barcode processing is in progress (prevents race condition)
  const barcodeProcessingRef = useRef(false);
  // Track the last successful scan time to prevent rapid duplicate scans
  const lastScanTimeRef = useRef(0);
  // Track the last scanned value to prevent duplicate processing
  const lastScannedValueRef = useRef("");
  // Track pending scan to process after current one completes
  const pendingScanRef = useRef(null);
  
  // Offline mode state
  const [isOnline, setIsOnline] = useState(connectivityIsOnline());
  const [offlineReady, setOfflineReady] = useState(false);
  const [pendingOfflineSales, setPendingOfflineSales] = useState(0);
  
  // Sales History panel state
  const [showSalesHistory, setShowSalesHistory] = useState(false);
  
  // Customer Balance modal state
  const [customerBalanceOpen, setCustomerBalanceOpen] = useState(false);
  // Shelf-location lookup modal (cashier helper — read-only, no cart impact)
  const [shelfLookupOpen, setShelfLookupOpen] = useState(false);

  // Active customer for the current sale — picked at the top of the screen so
  // their discount % is reflected on every line item *before* checkout. Auto-
  // clears back to "Walk-in" after each completed sale (cashier safety).
  const [activeCustomer, setActiveCustomer] = useState(null);
  // Cashier-facing "till notice" — a per-customer alert set on the customer
  // profile that pops up the moment the cashier picks them at the till.
  const [tillNoticeCustomer, setTillNoticeCustomer] = useState(null);
  const [activeSalesperson, setActiveSalesperson] = useState(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerPickerQuery, setCustomerPickerQuery] = useState("");
  const [customersLoading, setCustomersLoading] = useState(false);
  
  // Enlarged product image modal state
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Product info popup state
  const [productInfoOpen, setProductInfoOpen] = useState(false);
  const [productInfoData, setProductInfoData] = useState(null);
  const [productInfoLoading, setProductInfoLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  
  // Quick service state
  const [quickServiceOpen, setQuickServiceOpen] = useState(null); // 'AIRTIME', 'ELECTRICITY', 'DSTV', 'VOUCHER' or null
  const [quickServiceAmount, setQuickServiceAmount] = useState("");
  const [vasNetwork, setVasNetwork] = useState("VODACOM");
  const [vasMobileNumber, setVasMobileNumber] = useState("");
  const [vasMeterNumber, setVasMeterNumber] = useState("");
  const [vasProcessing, setVasProcessing] = useState(false);
  const [vasResult, setVasResult] = useState(null); // store token/reference after purchase
  const [flashEnabled, setFlashEnabled] = useState(false);
  
  // Hold Sale state - stores up to 5 held transactions
  const [heldSales, setHeldSales] = useState(() => {
    // Load from localStorage on init
    try {
      const saved = localStorage.getItem('heldSales');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [holdSalesOpen, setHoldSalesOpen] = useState(false);
  
  // Company branding state
  const [companySettings, setCompanySettings] = useState(null);
  const salesScreenLogoUrl = companySettings?.logo_url
    ? (companySettings.logo_url.startsWith('/api')
        ? `${BACKEND_URL}${companySettings.logo_url}`
        : companySettings.logo_url)
    : null;
  
  // Call Manager state
  const [callingManager, setCallingManager] = useState(false);
  
  // Weight entry popup state
  const [weightPopupOpen, setWeightPopupOpen] = useState(false);
  const [directSaleProduct, setDirectSaleProduct] = useState(null);
  const [pendingWeightProduct, setPendingWeightProduct] = useState(null);
  const [weightInput, setWeightInput] = useState("");
  const weightInputRef = useRef(null);

  // Fix Barcode modal state
  const [fixBarcodeOpen, setFixBarcodeOpen] = useState(false);
  const [fixBarcodePlu, setFixBarcodePlu] = useState("");
  const [fixBarcodeNewBarcode, setFixBarcodeNewBarcode] = useState("");

  // Live POS monitor — admin tools see the cart in real time.
  const [lastAction, setLastAction] = useState(null);
  useEffect(() => {
    if (!cart || cart.length === 0) return;
    const last = cart[cart.length - 1];
    setLastAction(`Last item: ${last.name || "Item"} × ${last.quantity || 1}`);
  }, [cart]);
  const liveMonitorState = useCallback(() => {
    const accountPaymentLine = (cart || []).find((c) => c.is_account_payment);
    return {
      cart,
      customerId: accountPaymentLine?.customer_id || null,
      customerName: accountPaymentLine?.customer_name || null,
      lastAction,
      page: "Sales",
    };
  }, [cart, lastAction]);
  useLiveMonitor(true, liveMonitorState);
  const [fixBarcodeProduct, setFixBarcodeProduct] = useState(null);
  const [fixBarcodeResults, setFixBarcodeResults] = useState([]);
  const [fixBarcodeLoading, setFixBarcodeLoading] = useState(false);
  const [fixBarcodeSaving, setFixBarcodeSaving] = useState(false);

  // Focus search input
  const focusSearchInput = useCallback(() => {
    setTimeout(() => {
      if (searchInputRef.current && !checkoutOpen && !pettyCashOpen && !servicesOpen && !priceCheckOpen && !loadQuoteOpen && !weightPopupOpen) {
        searchInputRef.current.focus();
      }
    }, 50);
  }, [checkoutOpen, pettyCashOpen, servicesOpen, priceCheckOpen, loadQuoteOpen, weightPopupOpen]);

  useEffect(() => {
    focusSearchInput();
  }, [checkoutOpen, pettyCashOpen, servicesOpen, priceCheckOpen, loadQuoteOpen, weightPopupOpen, focusSearchInput]);

  // Check Flash VAS status
  useEffect(() => {
    axios.get(`${API}/flash/status`).then(r => setFlashEnabled(r.data.enabled)).catch(() => {});
  }, []);

  // ---------------------------------------------------------------------------
  // Active customer helpers — see top-of-file state declarations.
  // Discount % = customer-level (dynamic — see /api/customer-levels) +
  // per-customer override.
  // ---------------------------------------------------------------------------
  const { map: CUSTOMER_LEVEL_DISCOUNTS } = useCustomerLevels();

  const getCustomerDiscountPercent = (cust) => {
    if (!cust) return 0;
    const lvl = (CUSTOMER_LEVEL_DISCOUNTS[cust.customer_level] ?? CUSTOMER_LEVELS_FALLBACK[cust.customer_level]) || 0;
    const extra = Number(cust.discount_percent) || 0;
    return lvl + extra;
  };

  const activeCustomerDiscountPercent = getCustomerDiscountPercent(activeCustomer);

  // Does this product/customer combo qualify for the product's VAT-Exempt
  // Selling Price (Pricing & VAT tab)? Only when ALL of:
  //   • customer holds a valid VAT-exemption certificate (has_vat_exemption)
  //   • product is Exempt or VAT Exempt Eligible
  //   • product has a non-zero vat_exempt_price_incl_vat (legacy DB may
  //     still call it promotion_price_incl_vat — read both).
  const qualifiesForVatExemptPrice = (product, customer) => {
    if (!customer?.has_vat_exemption) return false;
    const cat = product?.vat_category || "Standard 15%";
    if (cat !== "Exempt" && cat !== "VAT Exempt Eligible") return false;
    const p = Number(
      product?.vat_exempt_price_incl_vat ?? product?.promotion_price_incl_vat ?? 0
    );
    return Number.isFinite(p) && p > 0;
  };

  const getVatExemptPrice = (product) => Number(
    product?.vat_exempt_price_incl_vat ?? product?.promotion_price_incl_vat ?? 0
  );

  // Central resolver: given a product and the active customer, return
  //   { unitPrice, mode }
  // where mode is:
  //   • "none"              — no exemption applies, use shelf price
  //   • "vat_exempt_price"  — product's dedicated VAT-Exempt Selling Price,
  //                            entered incl-VAT and stripped /1.15 at the till
  //   • "stripped"          — shelf/1.15 (product is exempt-eligible but has
  //                            no special second price captured yet)
  // In both exemption modes the returned figure is what the exempt customer
  // actually pays at the till (i.e. ex-VAT / 0% VAT).
  const resolveExemptPricing = (product, customer, shelfPrice) => {
    const shelf = Number(shelfPrice) || Number(getProductPrice(product)) || 0;
    if (!customer?.has_vat_exemption) return { unitPrice: shelf, mode: "none" };
    const cat = product?.vat_category || "Standard 15%";
    if (cat !== "Exempt" && cat !== "VAT Exempt Eligible") {
      return { unitPrice: shelf, mode: "none" };
    }
    const special = getVatExemptPrice(product);
    if (special > 0) {
      // Special price is stored incl-VAT (like the shelf) so strip 15%
      // to get the amount the exempt customer actually pays.
      return {
        unitPrice: Math.round((special / 1.15) * 100) / 100,
        mode: "vat_exempt_price",
      };
    }
    return { unitPrice: Math.round((shelf / 1.15) * 100) / 100, mode: "stripped" };
  };

  // Staff discount helper — returns the per-product `staff_discount_percent`
  // when BOTH the active customer is flagged as staff AND the product has
  // a non-zero staff %. This value silently REPLACES any promo / loyalty /
  // level discount on the line. Returns 0 when either flag is off.
  const getStaffDiscountPercent = (item) => {
    if (!activeCustomer?.is_staff) return 0;
    const pct = Number(item?.staff_discount_percent) || 0;
    return pct > 0 ? pct : 0;
  };

  // Helper: is this cart item currently on a promotional discount? On-promo
  // items must NOT stack with the customer-level auto-discount, otherwise a
  // shopper effectively gets "promo price minus another X%".
  const isItemOnPromo = (item) =>
    !!item?.plu_code && promoPluCodes.has(item.plu_code);

  // Resolve the best-known cost for a cart line. We take the HIGHEST of
  // every cost field on the product so the safety-net guard is always
  // conservative — protects against selling below the more expensive of
  // last-cost vs weighted-average-cost. Returns 0 if none set.
  const getItemCost = (item) => {
    if (!item) return 0;
    const candidates = [
      item.cost_price,
      item.last_cost_price_excl_vat,
      item.average_cost_price_excl_vat,
      item.cost_price_excl_vat,
    ]
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v > 0);
    return candidates.length ? Math.max(...candidates) : 0;
  };

  // Convert the shelf `unit_price` (incl VAT) to an excl-VAT figure so we
  // can compare like-for-like against the excl-VAT cost.
  const getItemSellingExcl = (item) => {
    const rate = Number(item?.vat_rate != null ? item.vat_rate : 15) / 100 || 0;
    return item.unit_price / (1 + rate);
  };

  // Live markup % on the shelf price (both figures excl VAT). Rounded to
  // 2dp so a floating-point 7.0000...001 still trips the ≤7% guard.
  const getItemMarkupPercent = (item) => {
    const cost = getItemCost(item);
    if (cost <= 0) return Infinity; // unknown cost → don't block discount
    const sellingExcl = getItemSellingExcl(item);
    return Math.round(((sellingExcl - cost) / cost) * 10000) / 100;
  };

  // SAFETY NET (thin-markup): cancel every discount on lines with markup
  // ≤ THIN_MARKUP_THRESHOLD_PCT. Staff discount bypasses this (staff prices
  // are intentional).
  const THIN_MARKUP_THRESHOLD_PCT = 7;
  const hasThinMarkup = (item) => getItemMarkupPercent(item) <= THIN_MARKUP_THRESHOLD_PCT;

  // Would applying `pct` % off this line push the unit price below cost?
  // Compares on the same excl-VAT basis so a 15% VAT stack doesn't hide a
  // below-cost sale.
  const wouldGoBelowCost = (item, pct) => {
    const cost = getItemCost(item);
    if (cost <= 0) return false;
    const discountedExcl = getItemSellingExcl(item) * (1 - pct / 100);
    return discountedExcl < cost;
  };

  // Compute the post-discount unit price for a given cart item under the
  // currently active customer. Honours the product's `no_discount` flag and
  // the on-promotion flag. Staff discount (per-product) takes precedence
  // and REPLACES every other discount for that line.
  //
  // GUARD: customer discount is silently CANCELLED per-line if:
  //   • the product's markup is ≤ 7% (thin margin — protect gross profit), or
  //   • applying the discount would push the unit price under cost.
  // Staff discount is intentional and bypasses both guards.
  const getDiscountedUnitPrice = (item) => {
    const staffPct = getStaffDiscountPercent(item);
    if (staffPct > 0) return item.unit_price * (1 - staffPct / 100);
    if (!activeCustomer || activeCustomerDiscountPercent <= 0) return item.unit_price;
    if (item?.no_discount) return item.unit_price;
    if (isItemOnPromo(item)) return item.unit_price;
    if (hasThinMarkup(item)) return item.unit_price;
    if (wouldGoBelowCost(item, activeCustomerDiscountPercent)) return item.unit_price;
    return item.unit_price * (1 - activeCustomerDiscountPercent / 100);
  };

  // Lazy-load the customer list the first time the picker is opened.
  const ensureCustomersLoaded = async () => {
    if (allCustomers.length > 0 || customersLoading) return;
    setCustomersLoading(true);
    try {
      const res = await axios.get(`${API}/customers`);
      setAllCustomers(Array.isArray(res.data) ? res.data : (res.data?.customers || []));
    } catch (e) {
      console.error("Failed to load customers:", e);
      toast.error("Could not load customer list");
    } finally {
      setCustomersLoading(false);
    }
  };

  const openCustomerPicker = () => {
    setCustomerPickerQuery("");
    setCustomerPickerOpen(true);
    ensureCustomersLoaded();
  };

  // Re-price the cart when the active customer changes so the correct
  // ex-VAT figure is shown IMMEDIATELY (not just at checkout). Handles:
  //   • Product with a VAT-Exempt Selling Price captured → use it
  //   • Product exempt-eligible without a special price → auto /1.15 strip
  //   • Non-exempt category or non-exempt customer → restore shelf price
  const repriceCartForCustomer = (cust) => {
    setCart((prev) => prev.map((item) => {
      const cat = item.vat_category || "Standard 15%";
      const isExemptCat = (cat === "Exempt" || cat === "VAT Exempt Eligible");
      const specialPrice = Number(item.vat_exempt_price_incl_vat || 0);
      const shelfPrice = Number(item._shelf_unit_price ?? item.unit_price);

      let newUnit;
      let vatExemptApplied = false;
      let vatStripped = false;

      if (cust?.has_vat_exemption && isExemptCat) {
        if (specialPrice > 0) {
          // Special price is entered incl-VAT (matching the shelf price
          // convention) so strip /1.15 to get the actual amount the
          // exempt customer pays.
          newUnit = Math.round((specialPrice / 1.15) * 100) / 100;
          vatExemptApplied = true;
        } else {
          newUnit = Math.round((shelfPrice / 1.15) * 100) / 100;
          vatStripped = true;
        }
      } else {
        newUnit = shelfPrice;
      }

      if (newUnit === Number(item.unit_price)
          && !!item.vat_exempt_price_applied === vatExemptApplied
          && !!item.vat_stripped_at_till === vatStripped) {
        return item; // no change
      }
      return {
        ...item,
        _shelf_unit_price: shelfPrice,
        unit_price: newUnit,
        total: newUnit * item.quantity * (1 - (item.discount_percent || 0) / 100),
        vat_exempt_price_applied: vatExemptApplied,
        vat_stripped_at_till: vatStripped,
      };
    }));
  };

  const handlePickCustomer = async (cust) => {
    // ── Fetch a FRESH copy of the customer before we act on it. ──────
    // The top-bar picker reads from a cached `allCustomers` list that
    // is loaded once when the page opens. If the office ticked
    // ‟Has VAT exemption” on the customer AFTER that cache was primed
    // (or edited them in another tab), the cached object still says
    // has_vat_exemption=false — the shelf price stayed on the sales
    // screen but the Checkout modal (which fetches by id) correctly
    // stripped VAT, causing the two screens to disagree. Fetching by
    // id here guarantees the till always uses the latest flag.
    let fresh = cust;
    if (cust?.id) {
      try {
        const r = await axios.get(`${API}/customers/${cust.id}`);
        fresh = { ...cust, ...(r.data || {}) };
      } catch (e) {
        // Best-effort — fall back to the cached object.
      }
    }
    // Blocked customer accounts cannot be used at the till. The office
    // ticks ‟Blocked” on a profile to stop any further sales (overdue,
    // dispute, etc.) — if we still allow it to be selected the cashier
    // could ring up a sale on an account that shouldn't be transacting.
    if (fresh?.is_blocked) {
      toast.error(
        `🚫 ${fresh.name || fresh.business_name || "This customer"}'s account is BLOCKED. Please speak to the office.`,
        { duration: 10000, className: "!bg-red-100 !border-red-400 !text-red-900 !font-bold text-base" },
      );
      setCustomerPickerOpen(false);
      return;
    }
    setActiveCustomer(fresh);
    setCustomerPickerOpen(false);
    repriceCartForCustomer(fresh);
    const pct = getCustomerDiscountPercent(fresh);
    toast.success(
      pct > 0
        ? `Customer: ${fresh.name || fresh.business_name} — ${pct}% discount applied`
        : `Customer: ${fresh.name || fresh.business_name}`
    );
    // Birthday greeting — if TODAY is the primary or secondary birthday on
    // this account, pop a big celebratory toast so the cashier can wish
    // them a happy birthday face-to-face. Handles both YYYY-MM-DD and
    // MM-DD stored formats + leap-day (29-Feb → 28-Feb fallback).
    //
    // IMPORTANT: `extract()` returns null when a birthday is not set, and
    // the leap-fallback is null on every day that isn't 28-Feb. Comparing
    // `primaryMD === leapFallback` where both are null evaluated to TRUE,
    // which made the toast fire for every customer without a birthday
    // on screen. We now build an explicit set of valid "today" strings
    // and only match non-null extracted MD values against it.
    const todaysBirthdayName = (() => {
      const today = new Date();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const todayMD = `${mm}-${dd}`;
      const todaySet = new Set([todayMD]);
      // On a non-leap 28-Feb, also treat 29-Feb birthdays as "today".
      if (mm === "02" && dd === "28") todaySet.add("02-29");

      const extract = (b) => {
        if (!b || typeof b !== "string") return null;
        if (b.length === 10) return b.slice(5);         // YYYY-MM-DD → MM-DD
        if (b.length === 5) return b;                    // MM-DD as-is
        return null;
      };
      const primaryMD = extract(fresh.birthday);
      const secondaryMD = extract(fresh.secondary_birthday);

      if (primaryMD && todaySet.has(primaryMD)) {
        return fresh.name || fresh.business_name;
      }
      if (secondaryMD && todaySet.has(secondaryMD)) {
        return fresh.secondary_name || fresh.name || "them";
      }
      return null;
    })();

    if (todaysBirthdayName) {
      toast.success(
        `🎂 Happy Birthday, ${todaysBirthdayName}! 🎉  —  wish them well from the till!`,
        { duration: 15000, className: "!bg-pink-100 !border-pink-400 !text-pink-900 !font-bold text-base" },
      );
    }
    // If a till-notice is set on the customer profile, surface it as a
    // blocking modal before the cashier keeps ringing items up.
    if ((fresh?.till_notice || "").trim()) {
      setTillNoticeCustomer(fresh);
    }
    focusSearchInput();
  };

  // Belt-and-suspenders: whenever the active customer changes (from ANY
  // code path — top-bar picker, deep-link, "New Sale" reset, etc.) make
  // sure every line already in the cart is repriced against the current
  // customer's has_vat_exemption flag. Without this the shelf price
  // could linger on-screen while the Checkout modal (which resolves VAT
  // per-item from the fresh customer) strips /1.15 — the exact bug the
  // user reported for KANARIESFONTEIN + GROUND LUCERN.
  useEffect(() => {
    repriceCartForCustomer(activeCustomer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCustomer?.id, activeCustomer?.has_vat_exemption]);

  const clearActiveCustomer = () => {
    setActiveCustomer(null);
    setCustomerPickerQuery("");
    repriceCartForCustomer(null);
    focusSearchInput();
  };

  const filteredPickerCustomers = (() => {
    const q = customerPickerQuery.trim().toLowerCase();
    if (!q) return allCustomers.slice(0, 50);
    return allCustomers
      .filter((c) => {
        const hay = [
          c.name, c.business_name, c.account_number, c.phone, c.email
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  })();

  useEffect(() => {
    if (weightPopupOpen && weightInputRef.current) {
      setTimeout(() => weightInputRef.current?.focus(), 100);
    }
  }, [weightPopupOpen]);

  useEffect(() => {
    const handlePageClick = (e) => {
      const isInteractive = e.target.closest('input, button, select, textarea, [role="dialog"], [role="menu"], .quantity-input');
      if (!isInteractive && searchInputRef.current) {
        focusSearchInput();
      }
    };
    document.addEventListener('click', handlePageClick);
    return () => document.removeEventListener('click', handlePageClick);
  }, [focusSearchInput]);

  const handleCallManager = async () => {
    if (callingManager) return;
    setCallingManager(true);
    try {
      await axios.post(`${API}/alerts/call-manager`, {
        cashier_name: user?.name || user?.full_name || user?.username || "Cashier",
        message: "Manager needed at register"
      });
      toast.success("Manager has been notified!");
    } catch (error) {
      toast.error("Could not reach manager");
    } finally {
      setCallingManager(false);
    }
  };

  useEffect(() => {
    const initOffline = async () => {
      await initOfflineDB();
      const hasProducts = await hasCachedProducts();
      setOfflineReady(hasProducts);
      const pendingCount = await getPendingOfflineSalesCount();
      setPendingOfflineSales(pendingCount);
    };
    initOffline();

    const handleStorageChange = async () => {
      const hasProducts = await hasCachedProducts();
      setOfflineReady(hasProducts);
    };

    // Probe-based connectivity (handles "WiFi up, internet down")
    const unsubOnline = subscribeOnline((nowOnline) => setIsOnline(nowOnline));
    const cacheCheckInterval = setInterval(handleStorageChange, 5000);

    return () => {
      unsubOnline();
      clearInterval(cacheCheckInterval);
    };
  }, []);

  useEffect(() => {
    fetchQuickAccessProducts();
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    applyPromotions();
  }, [cart]);

  // NOTE: Spend-reward vouchers are NO LONGER auto-added to the cart.
  // They are minted as separate physical voucher slips (POST
  // /api/vouchers/issue) AFTER the sale completes, so the customer can
  // gift them or redeem them on a future visit. See handleSaleComplete
  // below for the issuance + thermal print. This useEffect is kept only
  // to strip any stale voucher lines left over from the old flow when
  // the code is upgraded on a live cart.
  useEffect(() => {
    setCart((prev) => {
      const hasVoucher = prev.some((i) => i.is_voucher_reward);
      return hasVoucher ? prev.filter((i) => !i.is_voucher_reward) : prev;
    });
  }, [spendRewards]);

  const applyPromotions = async () => {
    if (cart.length === 0) {
      setAppliedPromotions([]);
      setPromotionDiscount(0);
      setPromoPluCodes(new Set());
      setSpendRewards([]);
      return;
    }
    try {
      const cartItems = cart.map(item => ({
        product_id: item.product_id,
        plu_code: item.plu_code,
        quantity: item.quantity,
        unit_price: item.unit_price,
        category_id: item.category_id || "",
        // Forward pass-through flags so the spend-reward promo apply
        // endpoint can skip services / cash withdrawals / voucher lines
        // from the qualifying-basket total. The product_id prefix
        // ("SERVICE-*") already covers most cases; these flags cover
        // edge-cases where the id doesn't follow the convention.
        is_service: !!item.is_service,
        is_cash_withdrawal: !!item.is_cash_withdrawal,
        is_voucher_reward: !!item.is_voucher_reward,
      }));
      const res = await axios.post(`${API}/promotions/apply`, cartItems);
      // Track which PLUs are on promotion so we can suppress the customer
      // auto-discount for those items (see getDiscountedUnitPrice and
      // customerDiscountPreview below).
      const onPromo = new Set(
        (res.data.items || [])
          .filter((it) => (it.discount_amount || 0) > 0)
          .map((it) => it.plu_code)
          .filter(Boolean)
      );
      setPromoPluCodes(onPromo);
      if (res.data.total_discount > 0) {
        setAppliedPromotions(res.data.applied_promotions);
        setPromotionDiscount(res.data.total_discount);
      } else {
        setAppliedPromotions([]);
        setPromotionDiscount(0);
      }
      // Spend-reward status: capture even the "not yet qualified" rows
      // so the till can show progress ("R432 / R500 for FREE R50 airtime").
      setSpendRewards(res.data.spend_rewards || []);
    } catch (error) {
      console.log("Promotion check skipped:", error.message);
      setPromoPluCodes(new Set());
      setSpendRewards([]);
    }
  };

  const fetchQuickAccessProducts = async () => {
    try {
      if (isOnline) {
        const response = await axios.get(`${API}/products/quick-access`);
        setQuickAccessProducts(response.data);
      } else {
        const cached = await getCachedProducts();
        if (cached && cached.length > 0) {
          setQuickAccessProducts(cached.slice(0, 20));
        }
      }
    } catch (error) {
      try {
        const cached = await getCachedProducts();
        if (cached && cached.length > 0) {
          setQuickAccessProducts(cached.slice(0, 20));
        }
      } catch (cacheError) {
        console.error("Error fetching cached products:", cacheError);
      }
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      if (isOnline) {
        const response = await axios.get(`${API}/products/search`, { params: { q: query } });
        setSearchResults(response.data);
      } else {
        const results = await searchCachedProducts(query);
        setSearchResults(results || []);
      }
    } catch (error) {
      const results = await searchCachedProducts(query);
      setSearchResults(results || []);
    } finally {
      setSearching(false);
    }
  };

  const handleBarcodeEnter = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Get the value from state (controlled input)
      const inputValue = searchQuery?.trim();
      
      if (!inputValue) {
        return;
      }
      
      // IMMEDIATELY clear state - this is a controlled input, so this clears the UI
      setSearchQuery("");
      setSearchResults([]);
      
      // Skip exact duplicate scans (same barcode within 200ms)
      const now = Date.now();
      if (inputValue === lastScannedValueRef.current && now - lastScanTimeRef.current < 200) {
        console.log('[Barcode] Duplicate scan ignored');
        return;
      }
      
      // Update tracking refs BEFORE any async work
      lastScanTimeRef.current = now;
      lastScannedValueRef.current = inputValue;
      
      // If already processing, queue this scan
      if (barcodeProcessingRef.current) {
        console.log('[Barcode] Queuing:', inputValue);
        pendingScanRef.current = inputValue;
        return;
      }
      
      // Process the scan
      await processBarcodeScan(inputValue);
    }
  };
  
  // Optimized barcode processing - single fast attempt with cache fallback
  const processBarcodeScan = async (inputValue) => {
    barcodeProcessingRef.current = true;
    
    console.log(`[Barcode] Scan: "${inputValue}"`);
    
    try {
      let products = [];
      
      if (isOnline) {
        try {
          // Single fast attempt with short timeout for responsive scanning
          const response = await axios.get(`${API}/products/search`, { 
            params: { q: inputValue },
            timeout: 3000  // 3 second timeout - if server is slower, use cache
          });
          products = response.data;
          console.log(`[Barcode] API found ${products.length} products`);
        } catch (err) {
          console.log(`[Barcode] API failed, using cache:`, err.message);
          // Immediate fallback to cache - don't retry, keeps scanning fast
          products = await searchCachedProducts(inputValue);
          const exactMatch = await getCachedProductByCode(inputValue);
          if (exactMatch && !products.find(p => p.id === exactMatch.id)) {
            products.unshift(exactMatch);
          }
          if (products.length > 0) {
            toast.info("Using cached data");
          }
        }
      } else {
        products = await searchCachedProducts(inputValue);
        const exactMatch = await getCachedProductByCode(inputValue);
        if (exactMatch && !products.find(p => p.id === exactMatch.id)) {
          products.unshift(exactMatch);
        }
      }
      
      // Check for exact match with TIERED priority — our PLU first, then
      // barcodes, then supplier_plu. This mirrors the backend tiering and
      // protects against the case where the search returned multiple
      // products and find() would otherwise pick whichever came first.
      const exactMatch =
        products.find(p => p.plu_code === inputValue) ||
        products.find(p => p.barcode === inputValue) ||
        products.find(p => p.barcodes && p.barcodes.includes(inputValue)) ||
        products.find(p => p.supplier_plu === inputValue);
      
      if (exactMatch) {
        console.log(`[Barcode] Match: ${exactMatch.name}`);
        handleAddProduct(exactMatch);
      } else if (products.length === 1) {
        console.log(`[Barcode] Single result: ${products[0].name}`);
        handleAddProduct(products[0]);
      } else if (products.length > 1) {
        console.log(`[Barcode] Multiple results: ${products.length}`);
        setSearchResults(products);
        setSearchQuery(inputValue);
        toast.info(`${products.length} products found - select one`);
      } else {
        console.log(`[Barcode] Not found: "${inputValue}"`);
        toast.error(`Product not found: ${inputValue}`);
      }
    } catch (error) {
      console.error("[Barcode] Error:", error);
      toast.error("Search failed");
    } finally {
      // CRITICAL: Release the lock immediately
      barcodeProcessingRef.current = false;
      
      // Process any queued scan after a micro-delay
      if (pendingScanRef.current) {
        const pendingValue = pendingScanRef.current;
        pendingScanRef.current = null;
        console.log('[Barcode] Processing queued:', pendingValue);
        setTimeout(() => processBarcodeScan(pendingValue), 20);
      }
    }
  };

  // Debounced search - ONLY for text searches (product name lookups)
  // Barcode scans are handled directly by handleBarcodeEnter on Enter key press
  useEffect(() => {
    // Skip empty queries
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    // Skip if barcode processing is in progress
    if (barcodeProcessingRef.current) {
      return;
    }
    
    // For numeric/barcode-like inputs, DON'T do debounced search
    // The scanner sends Enter which triggers handleBarcodeEnter directly
    // Only skip if it's purely numeric (actual barcode) - text queries with spaces should always search
    const looksLikeBarcode = /^\d+$/.test(searchQuery);
    if (looksLikeBarcode) {
      // Don't interfere - let the Enter handler process this
      return;
    }
    
    // Only debounce text searches (product name lookups)
    const delayDebounce = setTimeout(async () => {
      // Double-check we're not processing a barcode
      if (barcodeProcessingRef.current) return;
      
      handleSearch(searchQuery);
    }, 300);
    
    return () => clearTimeout(delayDebounce);
  }, [searchQuery, isOnline]);

  const getProductPrice = (product) => {
    if (product.selling_price_incl_vat && product.selling_price_incl_vat > 0) {
      return product.selling_price_incl_vat;
    }
    if (priceLevel === 1 && product.retail_price_level1 && product.retail_price_level1 > 0) {
      return product.retail_price_level1;
    }
    if (priceLevel === 2 && product.retail_price_level2 && product.retail_price_level2 > 0) {
      return product.retail_price_level2;
    }
    return product.retail_price_level1 || product.retail_price_level2 || 0;
  };

  const handleAddProduct = (product) => {
    if (product.sold_per_kg) {
      setPendingWeightProduct(product);
      setWeightInput("");
      setWeightPopupOpen(true);
    } else {
      addToCart(product, 1);
    }
  };

  const confirmWeightAndAdd = () => {
    const weight = parseFloat(weightInput);
    if (!weight || weight <= 0) {
      toast.error("Please enter a valid weight");
      return;
    }
    addToCart(pendingWeightProduct, weight);
    setWeightPopupOpen(false);
    setPendingWeightProduct(null);
    setWeightInput("");
  };

  const warnIfNegativeStock = (productName, currentStock, newQuantity) => {
    // Service items (cash withdrawal, loan payment, etc.) carry max_stock 999999 — skip them.
    // Use a small tolerance so 0.001 rounding noise doesn't trigger a warning.
    const stock = Number(currentStock);
    if (!Number.isFinite(stock) || stock >= 999999) return;
    if (newQuantity > stock + 0.0001) {
      const shortfall = Math.max(0, newQuantity - Math.max(0, stock));
      toast.warning(
        `Negative stock: ${productName} will be -${shortfall % 1 !== 0 ? shortfall.toFixed(2) : shortfall} after this sale`,
        { duration: 2500, position: "top-center", id: `negstock-${productName}` }
      );
    }
  };

  /**
   * Check whether a proposed cart qty would dip into the product's reserved
   * stock. If yes, open the override modal and call `apply` only after the
   * cashier successfully enters an override PIN. Returns `true` when the
   * caller should proceed immediately (no block), `false` when the caller
   * should bail out (override modal will handle the rest asynchronously).
   *
   * Service items (max_stock >= 999999) and products with no reservation
   * are passed through unchanged.
   */
  const guardReservedStock = (product, newCartQty, apply) => {
    const reservedQty = Number(product?.reserved_stock) || 0;
    if (reservedQty <= 0) return true;
    const currentStock = Number(product?.current_stock) || 0;
    if (currentStock >= 999999) return true;
    const availableUnreserved = Math.max(0, currentStock - reservedQty);
    // Allow if the qty still fits within the unreserved pool.
    if (newCartQty <= availableUnreserved + 0.0001) return true;
    // Otherwise pop the override modal.
    setReservedOverride({
      productName: product.name,
      requestedQty: newCartQty,
      availableUnreserved,
      reservedQty,
      reservedFor: product.reserved_for || "",
      reservationNote: product.reservation_note || "",
      apply,
    });
    return false;
  };

  const addToCart = async (product, quantity = 1) => {
    // Check if product is blocked or temp blocked
    if (product.is_blocked) {
      toast.error(`Cannot sell BLOCKED product: ${product.name}`);
      return;
    }
    if (product.is_temp_blocked) {
      toast.error(`Cannot sell TEMP BLOCKED product: ${product.name}`);
      return;
    }
    if (product.is_direct_code && !product.__direct) {
      setDirectSaleProduct(product);
      setSearchQuery("");
      setSearchResults([]);
      return;
    }

    // ── Bulletproof: always price the new line against a FRESH copy of
    // the active customer. The top-bar picker reads from an in-memory
    // cache that can go stale if the customer's VAT-Exemption flag was
    // toggled after the till loaded. Without this, the cart line would
    // stay at the shelf price on the sales screen while the Checkout
    // modal (which fetches by id) correctly stripped VAT → the two
    // screens disagreed. Fetching here guarantees they never do.
    let customerForPricing = activeCustomer;
    if (activeCustomer?.id) {
      try {
        const r = await axios.get(`${API}/customers/${activeCustomer.id}`);
        customerForPricing = { ...activeCustomer, ...(r.data || {}) };
        // Persist back to state so subsequent operations use the fresh flag too.
        if (customerForPricing.has_vat_exemption !== activeCustomer.has_vat_exemption) {
          setActiveCustomer(customerForPricing);
        }
      } catch (e) {
        // Best-effort — fall back to the cached object.
      }
    }

    const shelfPrice = getProductPrice(product);
    const { unitPrice: price, mode: exemptMode } = resolveExemptPricing(product, customerForPricing, shelfPrice);
    if (!price || price === 0) {
      toast.error("Price not set for this product");
      return;
    }

    const existingItem = cart.find(item => item.product_id === product.id);
    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      const applyExisting = () => {
        warnIfNegativeStock(product.name, product.current_stock, newQuantity);
        setCart(cart.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: newQuantity, total: price * newQuantity * (1 - (item.discount_percent || 0) / 100) }
            : item
        ));
        setSearchQuery("");
        setSearchResults([]);
        focusSearchInput();
      };
      if (!guardReservedStock(product, newQuantity, applyExisting)) return;
      applyExisting();
      return;
    } else {
      const applyNew = () => {
        warnIfNegativeStock(product.name, product.current_stock, quantity);
        const newItem = {
          product_id: product.id,
          plu_code: product.plu_code,
          name: product.name,
          quantity: quantity,
          unit_price: price,
          // Always keep the untouched shelf figure so we can toggle back
          // when the exempt customer is cleared or swapped.
          _shelf_unit_price: shelfPrice,
          // Snapshot the exempt price + which mode we applied so
          // calculateTotals / CheckoutModal don't double-strip VAT.
          vat_exempt_price_incl_vat: Number(
            product.vat_exempt_price_incl_vat ?? product.promotion_price_incl_vat ?? 0
          ) || 0,
          vat_exempt_price_applied: exemptMode === "vat_exempt_price",
          vat_stripped_at_till: exemptMode === "stripped",
          discount_percent: 0,
          // Calculate max discount from markup if not explicitly set
          // Markup <= 20% = 5%, 20-30% = 7.5%, >30% = 10%, default to 10% if no markup
          max_discount_percent: product.no_discount ? 0 : (
            product.discount_percent_allowed > 0 
              ? product.discount_percent_allowed 
              : (product.markup_percent > 0 
                  ? (product.markup_percent <= 20 ? 5 : product.markup_percent <= 30 ? 7.5 : 10)
                  : 10)
          ),
          no_discount: product.no_discount || false,
          // Per-product Staff Discount % (silently applied when the active
          // customer is flagged as Staff — see getDiscountedUnitPrice /
          // calculateCustomerDiscount).
          staff_discount_percent: Number(product.staff_discount_percent) || 0,
          sold_per_kg: product.sold_per_kg || false,
          is_case_product: product.is_case_product || false,
          units_per_case: product.units_per_case || null,
          linked_product_id: product.linked_product_id || null,
          is_yard_stock: product.is_yard_stock || false,
          total: price * quantity,
          max_stock: product.current_stock,
          vat_category: product.vat_category || "Standard 15%",
          image_url: product.image_url || null,
          // Per-unit weight in kg (from Stock Master "Weight per Unit (kg)"). 0 means unknown.
          weight_kg: Number(product.weight_kg) || 0,
          // Snapshot reservation info so qty-edit can re-check without
          // re-fetching the product. Updated again on the next refresh.
          reserved_stock: Number(product.reserved_stock) || 0,
          reserved_for: product.reserved_for || "",
          reservation_note: product.reservation_note || "",
          ...(product.__direct || {}),
        };
        setCart([...cart, newItem]);
        setSelectedCartItem(newItem.product_id);
        setSearchQuery("");
        setSearchResults([]);
        focusSearchInput();
      };
      if (!guardReservedStock(product, quantity, applyNew)) return;
      applyNew();
      return;
    }

    setSearchQuery("");
    setSearchResults([]);
    focusSearchInput();
  };

  const handleAddDirectLine = ({ description, qty, price_incl, line }) => {
    const code = directSaleProduct;
    setDirectSaleProduct(null);
    addToCart({
      ...code,
      id: `DIRECT-${line?.id || Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: description,
      selling_price_incl_vat: price_incl,
      retail_price_level1: price_incl,
      vat_exempt_price_incl_vat: 0,
      promotion_price_incl_vat: 0,
      __direct: {
        is_direct_item: true,
        direct_product_id: code.id,
        direct_line_id: line?.id || null,
        direct_code_name: code.name,
        direct_cost_price: line ? line.unit_cost_excl : null,
      },
    }, qty);
  };

  const addServiceToCart = (serviceItem) => {
    setCart([...cart, serviceItem]);
    setServicesOpen(false);
  };

  // Quick service handler - adds service directly to cart via Flash VAS API
  const handleQuickServiceAdd = async () => {
    const baseAmount = parseFloat(quickServiceAmount);
    
    if (!baseAmount || baseAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const serviceTypes = {
      'AIRTIME': { name: 'Airtime', markup: 0 },
      'ELECTRICITY': { name: 'Electricity', markup: 0 },
      'DSTV': { name: 'DSTV', markup: 0 },
      'VOUCHER': { name: 'Voucher', markup: 5 }
    };

    const serviceType = serviceTypes[quickServiceOpen];
    if (!serviceType) return;

    // Airtime — call Flash VAS (voucher, no phone number needed)
    if (quickServiceOpen === 'AIRTIME') {
      setVasProcessing(true);
      try {
        const res = await axios.post(`${API}/flash/airtime/purchase`, {
          amount: baseAmount,
          network: vasNetwork,
        });
        if (res.data.success) {
          setVasResult(res.data);
          const serviceItem = {
            product_id: `SERVICE-AIRTIME-${Date.now()}`,
            plu_code: 'SVC-AIRTIME',
            name: `Airtime ${vasNetwork} R${baseAmount.toFixed(2)}`,
            quantity: 1, unit_price: baseAmount, cost_price: baseAmount,
            discount_percent: 0, max_discount_percent: 0, no_discount: true,
            sold_per_kg: false, total: baseAmount, max_stock: 999999,
            vat_category: "Zero-rated 0%", is_service: true,
            service_type: 'AIRTIME', base_amount: baseAmount,
            markup_percent: 0, profit: 0,
            flash_reference: res.data.reference,
            flash_pin: res.data.pin,
            flash_mock: res.data.mock,
          };
          setCart(prev => [...prev, serviceItem]);
          toast.success(`${vasNetwork} R${baseAmount.toFixed(2)} voucher added${res.data.mock ? ' (TEST MODE)' : ''}`);
          resetVasState();
        } else {
          toast.error(res.data.message || "Airtime purchase failed");
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Airtime purchase failed");
      } finally {
        setVasProcessing(false);
      }
      return;
    }

    // Electricity — call Flash VAS
    if (quickServiceOpen === 'ELECTRICITY') {
      if (!vasMeterNumber || vasMeterNumber.length < 5) {
        toast.error("Please enter a valid meter number");
        return;
      }
      setVasProcessing(true);
      try {
        const res = await axios.post(`${API}/flash/electricity/purchase`, {
          meter_number: vasMeterNumber,
          amount: baseAmount,
        });
        if (res.data.success) {
          setVasResult(res.data);
          const serviceItem = {
            product_id: `SERVICE-ELECTRICITY-${Date.now()}`,
            plu_code: 'SVC-ELECTRICITY',
            name: `Electricity ${vasMeterNumber} - R${baseAmount.toFixed(2)}`,
            quantity: 1, unit_price: baseAmount, cost_price: baseAmount,
            discount_percent: 0, max_discount_percent: 0, no_discount: true,
            sold_per_kg: false, total: baseAmount, max_stock: 999999,
            vat_category: "Zero-rated 0%", is_service: true,
            service_type: 'ELECTRICITY', base_amount: baseAmount,
            markup_percent: 0, profit: 0,
            flash_reference: res.data.reference,
            flash_token: res.data.token, flash_units: res.data.units,
            flash_mock: res.data.mock,
          };
          setCart(prev => [...prev, serviceItem]);
          const tokenMsg = res.data.token ? ` Token: ${res.data.token}` : '';
          toast.success(`Electricity R${baseAmount.toFixed(2)}${tokenMsg}${res.data.mock ? ' (TEST MODE)' : ''}`);
          resetVasState();
        } else {
          toast.error(res.data.message || "Electricity purchase failed");
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Electricity purchase failed");
      } finally {
        setVasProcessing(false);
      }
      return;
    }

    // Default flow for DSTV, VOUCHER (no Flash API call)
    const sellingPrice = baseAmount * (1 + serviceType.markup / 100);
    const profit = sellingPrice - baseAmount;

    const serviceItem = {
      product_id: `SERVICE-${quickServiceOpen}-${Date.now()}`,
      plu_code: `SVC-${quickServiceOpen}`,
      name: `${serviceType.name} - R${baseAmount.toFixed(2)}`,
      quantity: 1, unit_price: sellingPrice, cost_price: baseAmount,
      discount_percent: 0, max_discount_percent: 0, no_discount: true,
      sold_per_kg: false, total: sellingPrice, max_stock: 999999,
      vat_category: "Zero-rated 0%", is_service: true,
      service_type: quickServiceOpen, base_amount: baseAmount,
      markup_percent: serviceType.markup, profit: profit,
    };

    setCart([...cart, serviceItem]);
    toast.success(`Added ${serviceType.name} R${baseAmount.toFixed(2)} to cart`);
    resetVasState();
  };

  const resetVasState = () => {
    setQuickServiceAmount("");
    setVasMobileNumber("");
    setVasMeterNumber("");
    setVasNetwork("VODACOM");
    setVasResult(null);
    setQuickServiceOpen(null);
    focusSearchInput();
  };

  // ---- LOAN FEATURE ----
  const openLoanDialog = async () => {
    try {
      const res = await axios.get(`${API}/settings/loan-interest-rate`);
      setLoanInterestRate(res.data.loan_interest_rate || 20);
    } catch { /* use default */ }
    setLoanAmount("");
    setLoanFromTill(null);
    setLoanOpen(true);
  };

  const openWithdrawalDialog = async () => {
    try {
      const res = await axios.get(`${API}/settings/loan-interest-rate`);
      setWithdrawalFeeRate(res.data.withdrawal_fee_rate ?? 5);
    } catch { /* use default */ }
    setWithdrawalAmount("");
    setWithdrawalFromTill(null);
    setCashWithdrawalOpen(true);
  };

  const handleAddLoan = () => {
    const principal = parseFloat(loanAmount);
    if (!principal || principal <= 0) {
      toast.error("Enter a valid loan amount");
      return;
    }
    if (loanFromTill === null) {
      toast.error("Please select where the cash comes from (Till or Back Safe)");
      return;
    }
    const interest = round2(principal * (loanInterestRate / 100));
    const total = round2(principal + interest);

    const loanItem = {
      product_id: `LOAN-${Date.now()}`,
      plu_code: "SVC-LOAN",
      name: `LOAN R${principal.toFixed(2)} + ${loanInterestRate}% interest${!loanFromTill ? ' (Safe)' : ''}`,
      quantity: 1,
      unit_price: total,
      cost_price: principal,
      discount_percent: 0,
      max_discount_percent: 0,
      no_discount: true,
      sold_per_kg: false,
      total: total,
      max_stock: 999999,
      vat_category: "Zero-rated 0%",
      is_service: true,
      is_loan: true,
      service_type: "LOAN",
      base_amount: principal,
      loan_principal: principal,
      loan_interest: interest,
      loan_interest_rate: loanInterestRate,
      loan_from_till: loanFromTill,
      markup_percent: loanInterestRate,
      profit: interest,
    };

    setCart([...cart, loanItem]);
    toast.success(`Loan added: R${principal.toFixed(2)} + R${interest.toFixed(2)} interest = R${total.toFixed(2)} (${loanFromTill ? 'from till' : 'from safe'})`);
    setLoanOpen(false);
    setLoanAmount("");
  };

  const handleAddWithdrawal = () => {
    const amount = parseFloat(withdrawalAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid withdrawal amount");
      return;
    }
    if (withdrawalFromTill === null) {
      toast.error("Please select where the cash comes from (Till or Back Safe)");
      return;
    }
    const fee = round2(amount * (withdrawalFeeRate / 100));
    const total = round2(amount + fee);

    const withdrawalItem = {
      product_id: `WITHDRAWAL-${Date.now()}`,
      plu_code: "SVC-CASHOUT",
      name: `CASH WITHDRAWAL R${amount.toFixed(2)} + ${withdrawalFeeRate}% fee${!withdrawalFromTill ? ' (Safe)' : ''}`,
      quantity: 1,
      unit_price: total,
      cost_price: amount,
      discount_percent: 0,
      max_discount_percent: 0,
      no_discount: true,
      sold_per_kg: false,
      total: total,
      max_stock: 999999,
      vat_category: "Zero-rated 0%",
      is_service: true,
      is_cash_withdrawal: true,
      service_type: "CASH_WITHDRAWAL",
      base_amount: amount,
      withdrawal_amount: amount,
      withdrawal_fee: fee,
      withdrawal_fee_rate: withdrawalFeeRate,
      withdrawal_from_till: withdrawalFromTill,
      markup_percent: withdrawalFeeRate,
      profit: fee,
    };

    setCart([...cart, withdrawalItem]);
    toast.success(`Withdrawal: R${amount.toFixed(2)} + R${fee.toFixed(2)} fee = R${total.toFixed(2)} (${withdrawalFromTill ? 'from till' : 'from safe'})`);
    setCashWithdrawalOpen(false);
    setWithdrawalAmount("");
  };

  const round2 = (v) => Math.round(v * 100) / 100;

  const addAccountPaymentToCart = (accountPaymentItem) => {
    // Check if account payment for same customer already exists
    const existingPayment = cart.find(item => 
      item.is_account_payment && item.customer_id === accountPaymentItem.customer_id
    );
    
    if (existingPayment) {
      // Update existing payment amount
      const newTotal = existingPayment.total + accountPaymentItem.total;
      setCart(cart.map(item =>
        item.product_id === existingPayment.product_id
          ? { ...item, unit_price: newTotal, total: newTotal }
          : item
      ));
      toast.info(`Updated account payment for ${accountPaymentItem.customer_name}`);
    } else {
      // Add new account payment item
      setCart([...cart, accountPaymentItem]);
    }
    setCustomerBalanceOpen(false);
    focusSearchInput();
  };

  const updateCartItemQuantity = (productId, newQuantity) => {
    const item = cart.find(i => i.product_id === productId);
    if (!item) return;
    // Allow decimals for sold_per_kg products OR products sold in bulk (like tons)
    // Keep 3 decimal places for precision
    const roundedQuantity = Math.round(newQuantity * 1000) / 1000;
    if (roundedQuantity === 0) {
      removeFromCart(productId);
      return;
    }
    const applyQty = () => {
      warnIfNegativeStock(item.name, item.max_stock, roundedQuantity);
      setCart(cart.map(it =>
        it.product_id === productId
          ? { ...it, quantity: roundedQuantity, total: it.unit_price * roundedQuantity * (1 - (it.discount_percent || 0) / 100) }
          : it
      ));
      focusSearchInput();
    };
    // Re-use the same reservation guard via a synthetic product shape
    // built from the cart-item snapshot taken at add-time.
    const productShape = {
      name: item.name,
      current_stock: item.max_stock,
      reserved_stock: item.reserved_stock || 0,
      reserved_for: item.reserved_for || "",
      reservation_note: item.reservation_note || "",
    };
    if (!guardReservedStock(productShape, roundedQuantity, applyQty)) return;
    applyQty();
  };

  const updateCartItemPrice = (productId, newUnitPrice) => {
    if (Number.isNaN(newUnitPrice) || newUnitPrice <= 0) {
      toast.error("Please enter a valid selling price");
      return;
    }

    setCart(cart.map(item =>
      item.product_id === productId
        ? {
            ...item,
            unit_price: newUnitPrice,
            total: newUnitPrice * item.quantity * (1 - (item.discount_percent || 0) / 100)
          }
        : item
    ));
    focusSearchInput();
  };

  const handlePriceClick = (productId, currentPrice) => {
    setEditingPriceProductId(productId);
    setEditingPriceValue(currentPrice.toFixed(2));
    setTimeout(() => priceInputRef.current?.select(), 50);
  };

  const handlePriceSubmit = (productId) => {
    const newPrice = parseFloat(editingPriceValue);
    const item = cart.find((i) => i.product_id === productId);
    setEditingPriceProductId(null);
    setEditingPriceValue("");
    if (Number.isNaN(newPrice) || newPrice <= 0 || !item || Math.abs(newPrice - item.unit_price) < 0.005) {
      focusSearchInput();
      return;
    }
    if (newPrice < item.unit_price) {
      setPendingPriceOverride({ productId, name: item.name, oldPrice: item.unit_price, newPrice });
      return;
    }
    updateCartItemPrice(productId, newPrice);
  };

  const confirmPriceOverride = (reason) => {
    const { productId, oldPrice, newPrice } = pendingPriceOverride;
    updateCartItemPrice(productId, newPrice);
    setCart((prev) => prev.map((i) => (i.product_id === productId
      ? { ...i, price_override_reason: reason, price_override_original: i.price_override_original ?? oldPrice }
      : i)));
    setPendingPriceOverride(null);
  };

  const cancelPriceOverride = () => {
    setPendingPriceOverride(null);
    focusSearchInput();
  };

  const handlePriceCancel = () => {
    setEditingPriceProductId(null);
    setEditingPriceValue("");
    focusSearchInput();
  };

  // Handle clicking on quantity to edit it directly
  const handleQtyClick = (productId, currentQty) => {
    setEditingQtyProductId(productId);
    setEditingQtyValue(currentQty.toString());
    setTimeout(() => qtyInputRef.current?.select(), 50);
  };

  // Handle quantity edit submission
  const handleQtySubmit = (productId) => {
    const newQty = parseFloat(editingQtyValue);
    if (!isNaN(newQty) && newQty !== 0) {
      updateCartItemQuantity(productId, newQty);
    }
    setEditingQtyProductId(null);
    setEditingQtyValue("");
    focusSearchInput();
  };

  // Handle quantity edit cancel
  const handleQtyCancel = () => {
    setEditingQtyProductId(null);
    setEditingQtyValue("");
    focusSearchInput();
  };

  const updateCartItemDiscount = (productId, discountPercent) => {
    const item = cart.find(i => i.product_id === productId);
    if (!item) return;
    const maxDiscount = item.max_discount_percent || 0;
    let newDiscount = parseFloat(discountPercent) || 0;
    if (newDiscount < 0) newDiscount = 0;
    if (newDiscount > maxDiscount) {
      toast.error(`Maximum discount: ${maxDiscount}%`);
      newDiscount = maxDiscount;
    }
    if (item.no_discount && newDiscount > 0) {
      toast.error("No discount allowed");
      return;
    }
    // SAFETY NETS: apply the same rules that protect customer-level
    // discounts to per-line manual cashier discounts. Staff prices are
    // intentional and bypass these guards.
    const staffPct = getStaffDiscountPercent(item);
    if (newDiscount > 0 && staffPct <= 0) {
      if (hasThinMarkup(item)) {
        toast.error(`No discount allowed — markup is ≤ ${THIN_MARKUP_THRESHOLD_PCT}% on this item`);
        newDiscount = 0;
      } else if (wouldGoBelowCost(item, newDiscount)) {
        // Clamp to the largest discount that keeps the unit at cost.
        const cost = getItemCost(item);
        const sellingExcl = getItemSellingExcl(item);
        const maxByCost = Math.max(0, Math.floor(((sellingExcl - cost) / sellingExcl) * 100 * 100) / 100);
        toast.error(`Discount would sell below cost — capped at ${maxByCost}%`);
        newDiscount = Math.min(newDiscount, maxByCost);
      }
    }
    const newTotal = item.unit_price * item.quantity * (1 - newDiscount / 100);
    setCart(cart.map(cartItem =>
      cartItem.product_id === productId
        ? { ...cartItem, discount_percent: newDiscount, total: newTotal }
        : cartItem
    ));
    focusSearchInput();
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
    if (selectedCartItem === productId) setSelectedCartItem(null);
    focusSearchInput();
  };

  const clearCart = () => {
    setCart([]);
    setSelectedCartItem(null);
    focusSearchInput();
  };

  // Product info for cashier
  const fetchProductInfo = async (productId) => {
    setProductInfoLoading(true);
    setProductInfoOpen(true);
    setProductInfoData(null);
    try {
      const res = await axios.get(`${API}/products/${productId}/quick-info`);
      setProductInfoData(res.data);
    } catch {
      toast.error("Could not load product info");
      setProductInfoOpen(false);
    } finally {
      setProductInfoLoading(false);
    }
  };

  const translateToSepedi = async () => {
    if (!productInfoData?.id) return;
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/products/${productInfoData.id}/translate`, null, {
        params: { target_language: "Northern Sotho" }
      });
      setProductInfoData(prev => ({ ...prev, description_northern_sotho: res.data.translated }));
    } catch {
      toast.error("Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  const fetchInfoFromWeb = async () => {
    if (!productInfoData?.id) return;
    setProductInfoLoading(true);
    try {
      const res = await axios.post(`${API}/products/${productInfoData.id}/fetch-info`, {
        product_id: productInfoData.id,
        product_name: productInfoData.name,
      });
      const fetched = res.data.fetched_info;
      if (fetched?.description) {
        setProductInfoData(prev => ({ ...prev, description: fetched.description }));
        toast.success("Product info fetched!");
      } else {
        toast.info("No info found for this product");
      }
    } catch {
      toast.error("Could not fetch product info");
    } finally {
      setProductInfoLoading(false);
    }
  };

  // Hold Sale functions
  const handleHoldSale = () => {
    if (cart.length === 0) {
      toast.error("Cart is empty - nothing to hold");
      return;
    }
    
    if (heldSales.length >= 5) {
      toast.error("Maximum 5 held sales allowed. Please release one first.");
      return;
    }
    
    const heldSale = {
      id: `HOLD-${Date.now()}`,
      items: [...cart],
      total: cart.reduce((sum, item) => sum + item.total, 0),
      itemCount: cart.reduce((sum, item) => sum + item.quantity, 0),
      heldAt: new Date().toISOString(),
      heldAtDisplay: new Date().toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
    };
    
    const newHeldSales = [...heldSales, heldSale];
    setHeldSales(newHeldSales);
    localStorage.setItem('heldSales', JSON.stringify(newHeldSales));
    
    // Clear the cart
    setCart([]);
    setSelectedCartItem(null);
    
    toast.success(`Sale held (${heldSales.length + 1}/5) - ${heldSale.itemCount} item(s)`);
    focusSearchInput();
  };

  const handleReleaseSale = (heldSaleId) => {
    const saleToRelease = heldSales.find(s => s.id === heldSaleId);
    if (!saleToRelease) return;
    
    // Check if current cart has items
    if (cart.length > 0) {
      if (!window.confirm("Current cart has items. Replace with held sale?")) {
        return;
      }
    }
    
    // Restore the held sale to cart
    setCart(saleToRelease.items);
    setSelectedCartItem(null);
    
    // Remove from held sales
    const newHeldSales = heldSales.filter(s => s.id !== heldSaleId);
    setHeldSales(newHeldSales);
    localStorage.setItem('heldSales', JSON.stringify(newHeldSales));
    
    setHoldSalesOpen(false);
    toast.success(`Sale released - ${saleToRelease.itemCount} item(s) restored`);
    focusSearchInput();
  };

  const handleDeleteHeldSale = (heldSaleId) => {
    if (!window.confirm("Delete this held sale? Items will be lost.")) {
      return;
    }
    
    const newHeldSales = heldSales.filter(s => s.id !== heldSaleId);
    setHeldSales(newHeldSales);
    localStorage.setItem('heldSales', JSON.stringify(newHeldSales));
    
    toast.success("Held sale deleted");
  };

  // Fix Barcode functions
  const handleFixBarcodeLookup = async () => {
    const term = fixBarcodePlu.trim();
    if (!term) {
      toast.error("Type a PLU code or product description");
      return;
    }

    setFixBarcodeLoading(true);
    setFixBarcodeProduct(null);
    setFixBarcodeResults([]);

    try {
      const response = await axios.get(`${API}/products/paginated?search=${encodeURIComponent(term)}&limit=25`);
      const products = (response.data?.products) || [];

      if (products.length === 0) {
        toast.error(`No product found matching "${term}"`);
        return;
      }

      // Exact PLU match → auto-select to keep the legacy single-step flow fast
      const exactMatch = products.find(p => (p.plu_code || "") === term);
      if (exactMatch) {
        setFixBarcodeProduct(exactMatch);
        setFixBarcodeNewBarcode(exactMatch.barcode || "");
        setFixBarcodeResults([]);
        return;
      }

      // Otherwise show the picker
      if (products.length === 1) {
        const only = products[0];
        setFixBarcodeProduct(only);
        setFixBarcodeNewBarcode(only.barcode || "");
        setFixBarcodeResults([]);
      } else {
        setFixBarcodeResults(products);
      }
    } catch (error) {
      console.error("Error looking up product:", error);
      toast.error("Failed to look up product");
    } finally {
      setFixBarcodeLoading(false);
    }
  };

  const handleFixBarcodeSave = async () => {
    if (!fixBarcodeProduct || !fixBarcodeNewBarcode.trim()) {
      toast.error("Please enter a barcode");
      return;
    }
    
    setFixBarcodeSaving(true);
    
    try {
      await axios.put(`${API}/products/${fixBarcodeProduct.id}`, {
        barcode: fixBarcodeNewBarcode.trim()
      });
      
      toast.success(`Barcode updated for ${fixBarcodeProduct.name}`);
      
      // Reset and close modal
      setFixBarcodeOpen(false);
      setFixBarcodePlu("");
      setFixBarcodeNewBarcode("");
      setFixBarcodeProduct(null);
      setFixBarcodeResults([]);
      focusSearchInput();
    } catch (error) {
      console.error("Error saving barcode:", error);
      toast.error(error.response?.data?.detail || "Failed to save barcode");
    } finally {
      setFixBarcodeSaving(false);
    }
  };

  const calculateTotals = (customer = null) => {
    let subtotalExclVAT = 0;
    let totalVAT = 0;
    let totalDiscount = 0;

    cart.forEach(item => {
      if (!vatEnabled) {
        const discountAmount = item.unit_price * item.quantity * (item.discount_percent || 0) / 100;
        totalDiscount += discountAmount;
        subtotalExclVAT += item.total;
      } else {
        const vatCategory = item.vat_category || "Standard 15%";
        let itemVatRate = 0;
        // customerConditional captures whether this line's VAT still needs
        // to be stripped by calculateTotals (i.e. we HAVEN'T already done
        // it at add-to-cart / customer-pick time).
        let customerConditionalStripped = false;
        // Either the product's dedicated VAT-Exempt Selling Price was
        // substituted, or shelf/1.15 was applied when the exempt customer
        // was picked — in both cases the unit_price is ALREADY the final
        // ex-VAT figure the customer pays. Treat as 0% VAT and DO NOT
        // strip again (would double-discount the line).
        if (item.vat_exempt_price_applied || item.vat_stripped_at_till) {
          itemVatRate = 0;
        } else if (vatCategory === "Zero-rated 0%") {
          itemVatRate = 0;
        } else if (vatCategory === "VAT Exempt Eligible" || vatCategory === "Exempt") {
          if (customer && customer.has_vat_exemption) {
            itemVatRate = 0;
            customerConditionalStripped = true;
          } else {
            itemVatRate = vatRate;
          }
        } else {
          itemVatRate = vatRate;
        }
        const discountAmount = item.unit_price * item.quantity * (item.discount_percent || 0) / 100;
        totalDiscount += discountAmount;

        const shelfLineTotal = item.total; // incl 15% VAT (shelf price × qty)
        // Effective line total the exempt buyer actually pays.
        // If the product carries a dedicated VAT-Exempt Selling Price
        // (entered incl-VAT on the Pricing tab), use it — the price the
        // shop wants to charge the exempt customer, stripped of 15% VAT.
        // Otherwise strip the baked-in 15% VAT from the shelf price so
        // their total drops by the VAT amount they legitimately don't owe.
        let effectiveLineTotal;
        if (customerConditionalStripped) {
          const special = Number(item.vat_exempt_price_incl_vat) || 0;
          if (special > 0) {
            const discountFactor = 1 - (Number(item.discount_percent) || 0) / 100;
            effectiveLineTotal = (special / (1 + vatRate)) * item.quantity * discountFactor;
          } else {
            effectiveLineTotal = shelfLineTotal / (1 + vatRate);
          }
        } else {
          effectiveLineTotal = shelfLineTotal;
        }

        const itemTotalExclVAT = itemVatRate > 0
          ? effectiveLineTotal / (1 + itemVatRate)
          : effectiveLineTotal;
        const itemVAT = effectiveLineTotal - itemTotalExclVAT;
        subtotalExclVAT += itemTotalExclVAT;
        totalVAT += itemVAT;
      }
    });

    return {
      subtotalExclVAT,
      vat: totalVAT,
      totalInclVAT: subtotalExclVAT + totalVAT,
      totalDiscount
    };
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    // Validate that total is positive (for returns with negative qty, other items must offset)
    const currentTotal = totals.totalInclVAT - promotionDiscount;
    if (currentTotal <= 0) {
      toast.error("Transaction total must be positive. Add more items to offset the return.");
      return;
    }
    setCheckoutOpen(true);
  };

  const handleSaleComplete = async (finishedTxn) => {
    if (loadedQuotation) {
      axios.put(`${API}/quotations/${loadedQuotation.id}/status?status=ACCEPTED`).catch(console.error);
    }
    if (loadedOnlineOrder && finishedTxn?.id) {
      axios.post(`${API}/shop/admin/orders/${loadedOnlineOrder.id}/link-sale`, {
        sale_id: finishedTxn.id, sale_number: finishedTxn.transaction_number || finishedTxn.invoice_number,
      }).then(() => toast.success(`Online order ${loadedOnlineOrder.id} marked as collected`))
        .catch((e) => toast.error(`Sale saved, but online order ${loadedOnlineOrder.id} was not marked collected: ${e?.response?.data?.detail || "error"}`));
      setLoadedOnlineOrder(null);
    }

    // Mint + print a voucher slip for every spend-reward the basket qualified
    // for at checkout time. This replaces the old "auto-add to cart" flow.
    // Refunds / voids don't earn vouchers. Account sales don't earn vouchers
    // either — spend-reward promos are strictly for cash/card customers so
    // account holders can't stack the discount against their credit line.
    const txnType = (finishedTxn?.transaction_type || "").toUpperCase();
    const isRefund = txnType.includes("REFUND");
    const isAccountSale = txnType.includes("ACCOUNT");
    if (finishedTxn && !isRefund && !isAccountSale && spendRewards && spendRewards.length > 0) {
      const earned = spendRewards.filter((r) => r.qualified);
      for (const reward of earned) {
        try {
          const res = await axios.post(`${API}/vouchers/issue`, {
            promotion_id: reward.promotion_id,
            issued_sale_id: finishedTxn.id,
            issued_sale_number: finishedTxn.transaction_number || finishedTxn.invoice_number,
            issued_cashier: finishedTxn.cashier_name || finishedTxn.salesperson || null,
            issued_customer_id: finishedTxn.customer_id || null,
            issued_customer_name: finishedTxn.customer_name || null,
          });
          printVoucherSlip(res.data, {
            company_name: companySettings?.company_name,
            company_address: companySettings?.address,
            company_contact: companySettings?.phone,
          });
          toast.success(`Voucher printed: ${res.data.code} (${reward.voucher_description || `R${reward.voucher_value}`})`);
        } catch (err) {
          console.error("Voucher issue failed:", err);
          toast.error(`Could not issue voucher for "${reward.name}" — ${err?.response?.data?.detail || "unknown error"}`);
        }
      }
    }

    // Back-fill any voucher that was REDEEMED against this sale with the
    // completed sale id + number. At redemption time the sale doesn't exist
    // yet, so `redeemed_sale_number` is null on the voucher_slip; we patch
    // it here so audit reports and the Sales History can jump back and
    // forth. Fires-and-forgets — if the patch fails we still finish the
    // sale (voucher is already marked REDEEMED).
    if (finishedTxn && Array.isArray(cart)) {
      const redeemedCodes = new Set();
      for (const line of cart) {
        const code = line?.voucher_code || null;
        if (!code) continue;
        // Only the voucher-redemption marker lines carry this — spend
        // rewards on new sales have `is_voucher_reward` set instead.
        if (line.service_type === "VOUCHER_REDEEM" || line.service_type === "AIRTIME"
            || line.product_id?.startsWith("VOUCHER-REDEEM-")
            || line.product_id?.startsWith("VOUCHER-AIRTIME-")) {
          redeemedCodes.add(code);
        }
      }
      const saleNumber = finishedTxn.transaction_number || finishedTxn.invoice_number;
      for (const code of redeemedCodes) {
        axios.post(`${API}/vouchers/${encodeURIComponent(code)}/link-sale`, {
          sale_id: finishedTxn.id,
          sale_number: saleNumber,
        }).catch((err) => console.warn(`Failed to link voucher ${code} → sale`, err));
      }
    }

    setCart([]);
    setLoadedQuotation(null);
    setCheckoutOpen(false);
    setSelectedCartItem(null);
    // Auto-clear the active customer so the next sale starts fresh as Walk-in.
    setActiveCustomer(null);
    setActiveSalesperson(null);
    toast.success("Sale completed!");
    focusSearchInput();
  };

  const handleLoadQuotation = (cartItems, quotation) => {
    setCart(cartItems);
    setLoadedQuotation(quotation);
  };

  // "Sell" on Quotations opens the till with that quote in the cart (never auto-completes)
  const location = useLocation();
  const navigateTo = useNavigate();
  useEffect(() => {
    const onlineId = location.state?.loadOnlineOrderId;
    if (!onlineId) return;
    navigateTo(location.pathname, { replace: true, state: {} });
    (async () => {
      try {
        const { data: o } = await axios.get(`${API}/shop/admin/orders/${onlineId}`);
        if (o.pos_customer_id) await handlePickCustomer({ id: o.pos_customer_id, name: o.customer_name });
        // Normal shelf prices: the till applies the linked customer's discount itself.
        const items = await Promise.all(o.items.map(async (i) => {
          const { data: p } = await axios.get(`${API}/products/${i.product_id}`).catch(() => ({ data: {} }));
          return {
            ...p, product_id: i.product_id, plu_code: i.plu_code, name: i.name, quantity: i.quantity,
            unit_price: i.unit_price, total: Math.round(i.unit_price * i.quantity * 100) / 100,
            vat_category: p.vat_category, no_discount: p.no_discount || false,
          };
        }));
        setCart(items);
        setLoadedOnlineOrder(o);
        toast.success(`Loaded online order ${o.id} (${o.customer_name}). Take payment to complete it.`);
      } catch (e) {
        toast.error("Could not load the online order into the till");
      }
    })();
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const quoteId = location.state?.loadQuotationId;
    if (!quoteId) return;
    navigateTo(location.pathname, { replace: true, state: {} });
    (async () => {
      try {
        const { data: q } = await axios.get(`${API}/quotations/${quoteId}`);
        if (q.customer_id) await handlePickCustomer({ id: q.customer_id, name: q.customer_name });
        handleLoadQuotation(q.items.map((item) => ({
          product_id: item.product_id,
          plu_code: item.plu_code,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
          vat_category: item.vat_category,
          no_discount: item.no_discount || false,
          from_quotation: q.id,
        })), q);
        toast.success(`Loaded ${q.quotation_number} into the cart. Review and checkout when ready.`);
      } catch (e) {
        toast.error("Could not load quotation into the cart");
      }
    })();
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = calculateTotals();
  // Compute the active customer's preview discount on the cart so the running
  // grand total reflects what they'll actually pay. CheckoutModal still
  // recomputes from scratch on submit — this is purely for the cashier's
  // sidebar display.
  const customerDiscountPreview = (() => {
    if (!activeCustomer) return 0;
    return cart.reduce((sum, item) => {
      const lineTotal = Number(item.total) || (item.unit_price * item.quantity);
      const staffPct = getStaffDiscountPercent(item);
      if (staffPct > 0) {
        // Staff discount fully replaces any other discount on this line.
        return sum + (lineTotal * staffPct / 100);
      }
      if (activeCustomerDiscountPercent <= 0) return sum;
      if (item.no_discount) return sum;
      if (isItemOnPromo(item)) return sum;
      // Silently cancel this line's customer discount if it would sell below
      // cost — mirror the same guard used in getDiscountedUnitPrice().
      if (wouldGoBelowCost(item, activeCustomerDiscountPercent)) return sum;
      return sum + (lineTotal * activeCustomerDiscountPercent / 100);
    }, 0);
  })();
  const total = totals.totalInclVAT - promotionDiscount - customerDiscountPreview;

  // Total weight (kg) of cart items that have a weight_kg > 0 in Stock Master.
  // Products with no weight set contribute nothing. Shown to the cashier so they
  // can flag heavy collections / arrange for assistance loading the vehicle.
  const cartTotalWeightKg = cart.reduce((sum, item) => {
    const w = Number(item.weight_kg) || 0;
    if (w <= 0) return sum;
    return sum + (w * (Number(item.quantity) || 0));
  }, 0);

  // Heavy-load threshold from Company Settings (Cart Heavy-Load Warning card).
  // 0 / unset = disabled.
  const cartWeightWarningKg = Number(companySettings?.cart_weight_warning_kg) || 0;
  const cartWeightExceedsLimit = cartWeightWarningKg > 0 && cartTotalWeightKg >= cartWeightWarningKg;
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="h-[calc(100vh-var(--nav-chrome-h,96px))] overflow-hidden flex flex-col bg-slate-100" data-testid="sales-page">
      {/* TOP BAR - Input Fields */}
      <div className="bg-white border-b border-slate-200 px-3 py-1 flex items-end gap-2 shadow-sm z-20 flex-shrink-0">
        {/* Barcode/Search Input - Largest */}
        <div className="flex-1 max-w-xl relative">
          <label className="text-[10px] text-slate-500 uppercase font-semibold mb-0 block leading-tight">Scan Barcode/Enter PLU</label>
          <div className="relative">
            <Input
              type="text"
              placeholder="Scan or search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleBarcodeEnter}
              ref={searchInputRef}
              className="h-9 text-base font-mono border-2 border-slate-300 focus:border-blue-600 pr-10"
              data-testid="product-search-input"
              autoFocus
            />
            <Search className="absolute right-3 top-2 h-5 w-5 text-slate-400" />
          </div>
          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute z-50 mt-1 w-full max-w-xl bg-white border-2 border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
              {searchResults.map((product) => (
                <div
                  key={product.id}
                  onClick={() => { handleAddProduct(product); setSearchResults([]); }}
                  className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                  data-testid={`search-result-${product.id}`}
                >
                  <div>
                    <div className="font-semibold">{product.name}</div>
                    <div className="text-xs text-slate-500">{product.plu_code}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`text-sm font-medium px-2 py-0.5 rounded ${((product.current_stock || 0) - (product.reserved_stock || 0)) <= 0 ? 'bg-red-100 text-red-700' : ((product.current_stock || 0) - (product.reserved_stock || 0)) <= 5 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                      {((product.current_stock || 0) - (product.reserved_stock || 0)).toFixed(0)} avail
                      {product.reserved_stock > 0 && <span className="text-amber-600 ml-1">({product.reserved_stock} reserved)</span>}
                    </div>
                    <div className="text-lg font-bold text-slate-900 font-mono">R {getProductPrice(product).toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Items Count */}
        <div className="w-14">
          <label className="text-[10px] text-slate-500 uppercase font-semibold mb-0 block leading-tight">Items</label>
          <div className="h-9 flex items-center justify-center bg-blue-100 border-2 border-blue-300 rounded-md text-lg font-bold text-blue-800">
            {cart.length}
          </div>
        </div>

        {/* Quick Product Buttons */}
        <div className="flex-1 min-w-0 self-end">
          <QuickButtons onAddProduct={handleAddProduct} isAdmin={isAdmin} />
        </div>
      </div>

      {/* ACTIVE CUSTOMER BAR — picked here, discount kicks in immediately */}
      <div
        className={`flex items-center gap-2 px-4 py-1 border-b flex-shrink-0 z-10 ${
          activeCustomer
            ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300"
            : "bg-slate-50 border-slate-200"
        }`}
        data-testid="active-customer-bar"
      >
        <Users className={`h-5 w-5 ${activeCustomer ? "text-blue-700" : "text-slate-400"}`} />
        {activeCustomer ? (
          <>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-bold text-slate-900" data-testid="active-customer-name">
                {activeCustomer.name || activeCustomer.business_name || "Customer"}
              </span>
              <span className="text-[11px] text-slate-600 font-mono">
                {activeCustomer.account_number ? `#${activeCustomer.account_number} · ` : ""}
                {activeCustomer.customer_level || "Standard"}
              </span>
            </div>
            {activeCustomerDiscountPercent > 0 && (
              <span
                className="ml-2 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-bold shadow-sm"
                data-testid="active-customer-discount-badge"
              >
                {activeCustomerDiscountPercent}% OFF
              </span>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openCustomerPicker}
                className="h-8 text-xs"
                data-testid="change-customer-btn"
              >
                Change
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearActiveCustomer}
                className="h-8 text-xs text-red-600 hover:bg-red-50"
                data-testid="clear-customer-btn"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Walk-in
              </Button>
            </div>
          </>
        ) : (
          <>
            <span className="text-sm font-semibold text-slate-700">Walk-in customer</span>
            <span className="text-xs text-slate-500">— select a customer to apply their level discount automatically</span>
            <Button
              variant="default"
              size="sm"
              onClick={openCustomerPicker}
              className="ml-auto h-8 text-xs bg-blue-600 hover:bg-blue-700"
              data-testid="select-customer-btn"
            >
              <Users className="h-4 w-4 mr-1" /> Select Customer
            </Button>
          </>
        )}
      </div>

      {/* SALESPERSON BAR — credits the floor staff for commission */}
      <SalespersonSelector value={activeSalesperson} onChange={setActiveSalesperson} />

      {/* Live month-to-date earnings preview for the active salesperson */}
      <MyCommissionWidget salesperson={activeSalesperson} />

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex overflow-hidden p-2 gap-2" style={{ minHeight: 0 }}>
        {/* LEFT PANEL - Line Items Grid */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Data Grid - Takes full height */}
          <div className="flex-1 bg-white rounded-lg border-2 border-slate-200 shadow-sm overflow-hidden flex flex-col">
            {/* Grid Header */}
            <div className="bg-slate-800 text-white text-sm font-semibold uppercase tracking-wider flex items-center px-2 py-2 gap-2 flex-shrink-0">
              <div className="w-12 flex-shrink-0"></div>
              <div className="flex-1 min-w-0">Description</div>
              <div className="w-14 text-center">Qty</div>
              <div className="w-20 text-right">Retail</div>
              <div className="w-12 text-center">D%</div>
              <div className="w-24 text-right">Total</div>
              <div className="w-8"></div>
            </div>
            
            {/* Grid Body */}
            <div className={`flex-1 relative ${cart.length === 0 ? 'overflow-hidden' : 'overflow-y-auto'}`} style={{ minHeight: 0 }}>
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${cart.length === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                data-testid="sales-empty-logo-overlay"
              >
                {salesScreenLogoUrl ? (
                  <img
                    src={salesScreenLogoUrl}
                    alt="Company Logo"
                    className="w-[64%] h-[58%] -translate-y-[8%] object-contain opacity-60"
                    data-testid="sales-empty-company-logo"
                  />
                ) : (
                  <Package className="h-56 w-56 text-slate-300" data-testid="sales-empty-company-logo-fallback-icon" />
                )}
                <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 text-lg" data-testid="sales-empty-cart-message">Scan or search products to add to sale</p>
              </div>

              {cart.length === 0 ? (
                <div className="h-full" data-testid="sales-empty-cart-spacer" />
              ) : (
                [...cart].reverse().map((item, index) => (
                  <div
                    key={item.product_id}
                    onClick={() => setSelectedCartItem(item.product_id)}
                    className={`flex items-center px-2 py-2 gap-2 border-b border-slate-100 cursor-pointer transition-colors ${
                      selectedCartItem === item.product_id ? 'bg-blue-100 border-blue-300' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Product Image - Clickable for larger view */}
                    <div 
                      className="w-20 h-20 flex-shrink-0 rounded overflow-hidden bg-slate-100 border border-slate-200 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.image_url) {
                          setEnlargedImage({
                            url: item.image_url.startsWith('/api') ? `${BACKEND_URL}${item.image_url}` : item.image_url,
                            name: item.name,
                            plu: item.plu_code
                          });
                        }
                      }}
                      title={item.image_url ? "Click to enlarge" : "No image available"}
                    >
                      {item.image_url ? (
                        <img 
                          src={item.image_url.startsWith('/api') ? `${BACKEND_URL}${item.image_url}` : item.image_url} 
                          alt={item.name} 
                          className="w-full h-full object-contain bg-white"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className={`w-full h-full items-center justify-center text-slate-400 ${item.image_url ? 'hidden' : 'flex'}`}>
                        <Package className="h-8 w-8" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{item.name}</div>
                      <div className="text-xs text-slate-500">
                        {item.plu_code}
                        {item.is_case_product && item.units_per_case && (
                          <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                            CASE x{item.units_per_case}
                          </span>
                        )}
                        {Number(item.max_stock) < 999999 && item.quantity > Number(item.max_stock) + 0.0001 && (
                          <span
                            className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold border border-red-300"
                            title={`Stock on hand: ${item.max_stock}. After sale: ${(Number(item.max_stock) - item.quantity).toFixed(2)}`}
                            data-testid={`cart-item-negstock-${item.product_id}`}
                          >
                            ⚠ NEG STOCK ({(Number(item.max_stock) - item.quantity).toFixed(item.quantity % 1 !== 0 ? 2 : 0)})
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Clickable Quantity Cell */}
                    <div 
                      className="w-14 text-center font-mono font-bold text-lg cursor-pointer hover:bg-blue-100 rounded quantity-input"
                      onClick={(e) => { e.stopPropagation(); handleQtyClick(item.product_id, item.quantity); }}
                    >
                      {editingQtyProductId === item.product_id ? (
                        <input
                          ref={qtyInputRef}
                          type="number"
                          step="any"
                          value={editingQtyValue}
                          onChange={(e) => setEditingQtyValue(e.target.value)}
                          onBlur={() => handleQtySubmit(item.product_id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleQtySubmit(item.product_id);
                            if (e.key === 'Escape') handleQtyCancel();
                          }}
                          className="w-full h-8 text-center border-2 border-blue-500 rounded font-mono font-bold text-lg bg-white"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className="text-blue-600 underline decoration-dotted">
                          {item.quantity % 1 !== 0 ? item.quantity.toFixed(3) : item.quantity}
                        </span>
                      )}
                    </div>
                    {/* Clickable Price Cell — shows strike-through + discounted when an active customer applies */}
                    <div
                      className="w-20 text-right font-mono text-slate-700 cursor-pointer hover:bg-blue-100 rounded px-1"
                      onClick={(e) => { e.stopPropagation(); handlePriceClick(item.product_id, item.unit_price); }}
                      data-testid={`cart-item-price-cell-${item.product_id}`}
                    >
                      {editingPriceProductId === item.product_id ? (
                        <input
                          ref={priceInputRef}
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={editingPriceValue}
                          onChange={(e) => setEditingPriceValue(e.target.value)}
                          onBlur={() => handlePriceSubmit(item.product_id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handlePriceSubmit(item.product_id); }
                            if (e.key === 'Escape') handlePriceCancel();
                          }}
                          className="w-full h-8 text-right border-2 border-blue-500 rounded font-mono bg-white px-1"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`cart-item-price-input-${item.product_id}`}
                        />
                      ) : (() => {
                        const discounted = getDiscountedUnitPrice(item);
                        const hasCustomerDiscount = activeCustomer && activeCustomerDiscountPercent > 0 && !item.no_discount && discounted < item.unit_price;
                        const guardsActive = activeCustomer &&
                          activeCustomerDiscountPercent > 0 &&
                          !item.no_discount &&
                          !isItemOnPromo(item) &&
                          getStaffDiscountPercent(item) <= 0;
                        const thinMarkupHit = guardsActive && hasThinMarkup(item);
                        const belowCostGuardHit = guardsActive && !thinMarkupHit && wouldGoBelowCost(item, activeCustomerDiscountPercent);
                        if (hasCustomerDiscount) {
                          return (
                            <div className="flex flex-col items-end leading-tight">
                              <span className="text-[10px] text-slate-400 line-through">{item.unit_price.toFixed(2)}</span>
                              <span className="font-bold text-emerald-700">{discounted.toFixed(2)}</span>
                            </div>
                          );
                        }
                        return (
                          <span className="underline decoration-dotted inline-flex items-center gap-1">
                            {item.unit_price.toFixed(2)}
                            {(item.vat_exempt_price_applied || item.vat_stripped_at_till) && (
                              <span
                                title="Ex-VAT price — this customer is VAT-exempt on this product"
                                className="text-emerald-700 text-[10px] font-semibold bg-emerald-100 rounded px-1"
                                data-testid={`cart-vat-exempt-flag-${item.product_id}`}
                              >
                                0%
                              </span>
                            )}
                            {item.no_discount && activeCustomer && activeCustomerDiscountPercent > 0 && (
                              <span title="No discount allowed on this item" className="text-amber-600 text-[10px]">🔒</span>
                            )}
                            {thinMarkupHit && (
                              <span
                                title={`Customer discount cancelled — markup is ≤ ${THIN_MARKUP_THRESHOLD_PCT}%`}
                                className="text-orange-600 text-[10px]"
                                data-testid={`cart-thin-markup-flag-${item.product_id}`}
                              >
                                ⚠
                              </span>
                            )}
                            {belowCostGuardHit && (
                              <span
                                title="Customer discount cancelled on this line — would sell below cost"
                                className="text-red-600 text-[10px]"
                                data-testid={`cart-below-cost-flag-${item.product_id}`}
                              >
                                ⚠
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="w-12 text-center font-mono text-slate-600">
                      {(() => {
                        const lineDisc = Number(item.discount_percent) || 0;
                        const guardsActive = activeCustomer && activeCustomerDiscountPercent > 0 && !item.no_discount && !isItemOnPromo(item) && getStaffDiscountPercent(item) <= 0;
                        const guardHit = guardsActive && (hasThinMarkup(item) || wouldGoBelowCost(item, activeCustomerDiscountPercent));
                        const custDisc = (activeCustomer && !item.no_discount && !isItemOnPromo(item) && !guardHit) ? activeCustomerDiscountPercent : 0;
                        // Combined effective % shown for clarity. Underlying math:
                        // total = unit_price * qty * (1 - lineDisc/100) and the
                        // customer discount is applied at checkout.
                        const total = lineDisc + custDisc;
                        if (custDisc > 0) {
                          return <span className="text-emerald-700 font-semibold">{total}</span>;
                        }
                        return total;
                      })()}
                    </div>
                    <div className="w-24 text-right font-mono font-bold text-lg text-slate-900">
                      {(() => {
                        const baseTotal = Number(item.total) || (item.unit_price * item.quantity);
                        const custDisc = (activeCustomer && !item.no_discount && !isItemOnPromo(item)) ? activeCustomerDiscountPercent : 0;
                        const effective = custDisc > 0 ? baseTotal * (1 - custDisc / 100) : baseTotal;
                        if (custDisc > 0) {
                          return (
                            <div className="flex flex-col items-end leading-tight">
                              <span className="text-[10px] text-slate-400 line-through font-normal">{baseTotal.toFixed(2)}</span>
                              <span className="text-emerald-700">{effective.toFixed(2)}</span>
                            </div>
                          );
                        }
                        return effective.toFixed(2);
                      })()}
                    </div>
                    <div className="w-8 flex justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); fetchProductInfo(item.product_id); }}
                        className="h-7 w-7 p-0 hover:bg-blue-100"
                        title="Product Info"
                        data-testid={`cart-item-info-${item.product_id}`}
                      >
                        <Info className="h-4 w-4 text-blue-500" />
                      </Button>
                    </div>
                    <div className="w-8 flex justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); removeFromCart(item.product_id); }}
                        className="h-7 w-7 p-0 hover:bg-red-100"
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right sidebar - Action buttons */}
        <div className="w-64 flex flex-col flex-shrink-0 overflow-hidden">
          {/* Scrollable content */}
          <div className="flex-1 flex flex-col gap-1 overflow-y-auto pr-1">
            {/* TOTAL DISPLAY - Prominent at top */}
            <div className="bg-slate-900 rounded-lg p-2 text-center flex-shrink-0">
              <div className="text-slate-400 text-[10px] uppercase">VAT Incl. Total</div>
              <div className="text-2xl font-bold text-white font-mono tracking-tight leading-tight" data-testid="cart-total">
                R {total.toFixed(2)}
              </div>
              {customerDiscountPreview > 0 && (
                <div className="text-emerald-300 text-[10px] mt-0.5 flex items-center justify-center gap-1" data-testid="customer-discount-line">
                  <Percent className="h-3 w-3" />
                  Customer {activeCustomerDiscountPercent}%: -R {customerDiscountPreview.toFixed(2)}
                </div>
              )}
              {promotionDiscount > 0 && (
                <div className="text-yellow-400 text-[10px] mt-0.5 flex items-center justify-center gap-1">
                  <Tag className="h-3 w-3" />
                  Promo Discount: -R {promotionDiscount.toFixed(2)}
                </div>
              )}
              {/* Spend-reward progress / claim banner. Shows the best
                  (highest-value) active spend-reward promo — either "R68
                  to go for FREE R50 airtime" or "Claim FREE voucher"
                  once qualified. Tapping the claim chip adds a zero-
                  value voucher line to the cart so the till, receipt and
                  redemption ledger all agree. */}
              {spendRewards.length > 0 && (() => {
                const best = [...spendRewards].sort((a, b) => (b.voucher_value || 0) - (a.voucher_value || 0))[0];
                if (!best) return null;
                // Voucher slip prints AFTER the sale closes (no longer
                // added as a cart line). Show a "will print" chip while
                // the basket is qualifying.
                if (best.qualified) {
                  return (
                    <div
                      className="mt-1 mx-1 px-2 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center gap-1 shadow"
                      data-testid="spend-reward-earned"
                      title="A separate voucher slip will print at the end of the sale"
                    >
                      <Gift className="h-3 w-3" />
                      {best.voucher_description || `FREE R${best.voucher_value} voucher`} — prints at checkout
                    </div>
                  );
                }
                return (
                  <div
                    className="mt-1 mx-1 px-2 py-0.5 rounded bg-amber-500/90 text-white text-[10px] flex items-center justify-center gap-1 shadow"
                    data-testid="spend-reward-progress"
                    title={`Spend R${best.remaining.toFixed(0)} more to unlock ${best.voucher_description || `R${best.voucher_value} voucher`}`}
                  >
                    <Gift className="h-3 w-3" />
                    R {best.remaining.toFixed(0)} to go for {best.voucher_description || `FREE R${best.voucher_value}`}
                  </div>
                );
              })()}
              {cartTotalWeightKg > 0 && (
                <div
                  className={`text-[10px] mt-0.5 flex items-center justify-center gap-1 ${
                    cartWeightExceedsLimit ? "text-red-300 font-semibold" : "text-sky-300"
                  }`}
                  data-testid="cart-total-weight"
                >
                  <Scale className="h-3 w-3" />
                  Total weight: {cartTotalWeightKg.toFixed(2)} kg
                </div>
              )}
              {cartWeightExceedsLimit && (
                <div
                  className="mt-1 mx-1 px-2 py-0.5 rounded bg-red-600 text-white text-[10px] flex items-center justify-center gap-1 shadow"
                  data-testid="cart-weight-warning"
                  title={`Cart weight has reached the ${cartWeightWarningKg} kg threshold`}
                >
                  <Truck className="h-3 w-3" />
                  Heavy load — arrange loading help
                </div>
              )}
            </div>

            {/* END SALE Button */}
            <Button
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className="h-11 bg-green-600 hover:bg-green-700 text-white text-base font-bold shadow-lg flex items-center justify-center gap-2"
              data-testid="checkout-button"
            >
              <DollarSign className="h-5 w-5" />
              END SALE
            </Button>

            {/* Item Actions */}
            <div className="bg-white rounded-lg border border-slate-200 p-1 grid grid-cols-5 gap-1">
              <Button
                variant="outline"
                onClick={() => selectedCartItem && removeFromCart(selectedCartItem)}
                disabled={!selectedCartItem}
                className="h-9 flex flex-col items-center justify-center text-[9px] font-semibold border hover:bg-red-50 hover:border-red-300 hover:text-red-700"
              >
                <Trash2 className="h-3.5 w-3.5" />
                DEL
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedCartItem) {
                    const item = cart.find(i => i.product_id === selectedCartItem);
                    if (item) handleQtyClick(selectedCartItem, item.quantity);
                  }
                }}
                disabled={!selectedCartItem}
                className="h-9 flex flex-col items-center justify-center text-[9px] font-semibold border hover:bg-blue-50"
              >
                <Scale className="h-3.5 w-3.5" />
                QTY
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedCartItem) {
                    const item = cart.find(i => i.product_id === selectedCartItem);
                    if (item) handlePriceClick(selectedCartItem, item.unit_price);
                  }
                }}
                disabled={!selectedCartItem}
                className="h-9 flex flex-col items-center justify-center text-[9px] font-semibold border hover:bg-purple-50"
                data-testid="sales-edit-price-button"
              >
                <Tag className="h-3.5 w-3.5" />
                PRICE
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  if (selectedCartItem) {
                    const item = cart.find(i => i.product_id === selectedCartItem);
                    if (item?.no_discount) {
                      toast.error("No discount allowed on this item");
                      return;
                    }
                    setDiscountInput(item?.discount_percent?.toString() || "");
                    setDiscountOpen(true);
                  }
                }}
                disabled={!selectedCartItem || (cart.find(i => i.product_id === selectedCartItem)?.no_discount)}
                className="h-9 flex flex-col items-center justify-center text-[9px] font-semibold border hover:bg-green-50"
              >
                <Percent className="h-3.5 w-3.5" />
                DISC
              </Button>
              <Button
                variant="outline"
                onClick={clearCart}
                disabled={cart.length === 0}
                className="h-9 flex flex-col items-center justify-center text-[9px] font-semibold border hover:bg-amber-50 hover:border-amber-400 bg-amber-50 border-amber-200"
              >
                <X className="h-3.5 w-3.5" />
                CLEAR
              </Button>
            </div>

            {/* Print & Fix */}
            <div className="bg-white rounded-lg border border-slate-200 p-1 grid grid-cols-2 gap-1">
              <Button
                variant="outline"
                onClick={() => setPriceCheckOpen(true)}
                className="h-8 text-[10px] font-semibold border"
              >
                PRICE CHK
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setFixBarcodeOpen(true);
                  setFixBarcodePlu("");
                  setFixBarcodeNewBarcode("");
                  setFixBarcodeProduct(null);
                }}
                className="h-8 text-[10px] font-semibold border bg-amber-50 border-amber-200 hover:bg-amber-100"
                data-testid="fix-barcode-btn"
              >
                FIX BARCODE
              </Button>
            </div>

            {/* Hold Sale + Secondary Actions */}
            <div className="bg-white rounded-lg border border-slate-200 p-1 grid grid-cols-2 gap-1">
              <Button
                onClick={handleHoldSale}
                disabled={cart.length === 0 || heldSales.length >= 5}
                className="h-8 flex items-center justify-center gap-1 text-[10px] font-semibold bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Pause className="h-3 w-3" />
                HOLD
              </Button>
              <Button
                onClick={() => setHoldSalesOpen(true)}
                disabled={heldSales.length === 0}
                className={`h-8 flex items-center justify-center gap-1 text-[10px] font-semibold ${
                  heldSales.length > 0 
                    ? 'bg-cyan-500 hover:bg-cyan-600 text-white animate-pulse' 
                    : 'bg-slate-200 text-slate-400'
                }`}
              >
                <Play className="h-3 w-3" />
                REL ({heldSales.length}/5)
              </Button>
              <Button variant="outline" onClick={() => setLoadQuoteOpen(true)} className="h-8 text-[10px] font-semibold border">
                QUOTE
              </Button>
              <Button variant="outline" onClick={() => setShowSalesHistory(true)} className="h-8 text-[10px] font-semibold border">
                HISTORY
              </Button>
              <Button variant="outline" onClick={() => setPettyCashOpen(true)} className="h-8 text-[10px] font-semibold border bg-green-50 border-green-200 col-span-2">
                PETTY CASH
              </Button>
              <Button variant="outline" onClick={() => setPayCasualOpen(true)} className="h-8 text-[10px] font-semibold border bg-emerald-50 border-emerald-300 col-span-2" data-testid="till-pay-casual-btn">
                PAY CASUAL
              </Button>
            </div>

            {/* Quick Services */}
            <div className="bg-white rounded-lg border border-slate-200 p-1">
              <div className={`grid gap-1 ${flashEnabled ? 'grid-cols-4' : 'grid-cols-2'}`}>
                {flashEnabled && (
                  <>
                    <Button
                      onClick={() => { setQuickServiceOpen('AIRTIME'); setQuickServiceAmount(''); }}
                      className="h-9 flex flex-col items-center justify-center text-[8px] font-semibold bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      <Smartphone className="h-3.5 w-3.5" />
                      AIR
                    </Button>
                    <Button
                      onClick={() => { setQuickServiceOpen('ELECTRICITY'); setQuickServiceAmount(''); }}
                      className="h-9 flex flex-col items-center justify-center text-[8px] font-semibold bg-yellow-500 hover:bg-yellow-600 text-white"
                    >
                      <Zap className="h-3.5 w-3.5" />
                      ELEC
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => { setQuickServiceOpen('DSTV'); setQuickServiceAmount(''); }}
                  className="h-9 flex flex-col items-center justify-center text-[8px] font-semibold bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <Tv className="h-3.5 w-3.5" />
                  DSTV
                </Button>
                <Button
                  onClick={() => { setQuickServiceOpen('VOUCHER'); setQuickServiceAmount(''); }}
                  className="h-9 flex flex-col items-center justify-center text-[8px] font-semibold bg-green-500 hover:bg-green-600 text-white"
                >
                  <Ticket className="h-3.5 w-3.5" />
                  VCH
                </Button>
              </div>
            </div>

            {/* Price Levels */}
            <div className="bg-white rounded-lg border border-slate-200 p-1 grid grid-cols-2 gap-1">
              <Button
                variant={priceLevel === 1 ? "default" : "outline"}
                onClick={() => setPriceLevel(1)}
                className={`h-7 text-[10px] font-semibold ${priceLevel === 1 ? 'bg-slate-800' : ''}`}
              >
                RETAIL
              </Button>
              <Button
                variant={priceLevel === 2 ? "default" : "outline"}
                onClick={() => setPriceLevel(2)}
                className={`h-7 text-[10px] font-semibold ${priceLevel === 2 ? 'bg-slate-800' : ''}`}
              >
                LEVEL 2
              </Button>
            </div>

            {/* Quick Functions */}
            <div className="bg-white rounded-lg border border-slate-200 p-1 grid grid-cols-2 gap-1">
              <Button variant="outline" onClick={() => setServicesOpen(true)} className="h-7 text-[10px] font-semibold border">
                SERVICES
              </Button>
              <Button variant="outline" onClick={() => setAccountReceiptOpen(true)} className="h-7 text-[10px] font-semibold border">
                ACC RCP
              </Button>
              <Button 
                variant="outline" 
                onClick={openLoanDialog}
                className="h-7 text-[10px] font-semibold border bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                data-testid="loan-btn"
              >
                LOAN
              </Button>
              <Button 
                variant="outline" 
                onClick={openWithdrawalDialog}
                className="h-7 text-[10px] font-semibold border bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                data-testid="cash-withdrawal-btn"
              >
                CASH OUT
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setCustomerBalanceOpen(true)} 
                className="h-7 text-[10px] font-semibold border bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 col-span-2"
                data-testid="pay-account-btn"
              >
                <CreditCard className="h-3 w-3 mr-1" />
                PAY ACCOUNT
              </Button>
              <Button
                variant="outline"
                onClick={() => setRedeemVoucherOpen(true)}
                className="h-7 text-[10px] font-semibold border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 col-span-2"
                data-testid="redeem-voucher-btn"
              >
                <Gift className="h-3 w-3 mr-1" />
                REDEEM VOUCHER
              </Button>
              <Button
                variant="outline"
                onClick={() => setSellGiftVoucherOpen(true)}
                className="h-7 text-[10px] font-semibold border bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 col-span-2"
                data-testid="sell-gift-voucher-btn"
              >
                <Gift className="h-3 w-3 mr-1" />
                SELL GIFT VOUCHER
              </Button>
              <Button
                variant="outline"
                onClick={() => setShelfLookupOpen(true)}
                className="h-7 text-[10px] font-semibold border bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100 col-span-2"
                data-testid="shelf-location-btn"
              >
                <MapPin className="h-3 w-3 mr-1" />
                CHECK SHELF LOCATION
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/bin-assign'}
                className="h-7 text-[10px] font-semibold border bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100"
                data-testid="cashier-bin-assign-btn"
              >
                <MapPin className="h-3 w-3 mr-1" />
                BIN ASSIGN
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/stock-count'}
                className="h-7 text-[10px] font-semibold border bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100"
                data-testid="cashier-stock-count-btn"
              >
                <ClipboardCheck className="h-3 w-3 mr-1" />
                STOCK COUNT
              </Button>
            </div>

            {/* Call Manager */}
            <Button
              variant="outline"
              onClick={handleCallManager}
              disabled={callingManager}
              className="h-7 text-[10px] font-semibold border border-red-300 text-red-700 hover:bg-red-50"
            >
              <Phone className={`h-3 w-3 mr-1 ${callingManager ? 'animate-pulse' : ''}`} />
              {callingManager ? 'CALLING...' : 'CALL MANAGER'}
            </Button>
          </div>
        </div>
      </div>

      {/* MODALS */}
      {showSalesHistory && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/50" onClick={() => setShowSalesHistory(false)} />
          <div className="w-full max-w-2xl bg-white shadow-xl animate-in slide-in-from-right">
            <SalesHistoryPanel onClose={() => setShowSalesHistory(false)} />
          </div>
        </div>
      )}

      <PettyCashModal open={pettyCashOpen} onClose={() => setPettyCashOpen(false)} />
      <PayCasualModal open={payCasualOpen} onClose={() => setPayCasualOpen(false)} />
      <LoadQuotationModal open={loadQuoteOpen} onClose={() => setLoadQuoteOpen(false)} onLoadQuotation={handleLoadQuotation} />
      <AccountReceiptModal open={accountReceiptOpen} onClose={() => setAccountReceiptOpen(false)} />

      <ReservedStockOverrideModal
        payload={reservedOverride}
        onResolve={(approved) => {
          const pending = reservedOverride;
          setReservedOverride(null);
          if (approved && pending?.apply) pending.apply();
        }}
      />
      <ServicesModal open={servicesOpen} onClose={() => setServicesOpen(false)} onAddService={addServiceToCart} />
      <PriceCheckModal open={priceCheckOpen} onClose={() => setPriceCheckOpen(false)} />
      <SellGiftVoucherDialog
        open={sellGiftVoucherOpen}
        onClose={() => setSellGiftVoucherOpen(false)}
        cashier={user?.name || user?.full_name || user?.username || "Cashier"}
        store={{
          company_name: companySettings?.company_name,
          company_address: companySettings?.address,
          company_contact: companySettings?.phone,
        }}
      />
      <RedeemVoucherDialog
        open={redeemVoucherOpen}
        onClose={() => setRedeemVoucherOpen(false)}
        currentCashier={activeSalesperson?.name || null}
        onAddLine={(line) => {
          // Voucher redeem lines are always added to the cart. The airtime
          // variant (line.requires_airtime_purchase) also opens Services so
          // the cashier can finish the Flash top-up on the same slip.
          setCart((prev) => [...prev, { ...line, id: `voucher-redeem-${line.voucher_code}-${Date.now()}` }]);
          if (line.requires_airtime_purchase) {
            toast.info("Complete the airtime top-up in the Services dialog.");
            setServicesOpen(true);
          }
        }}
      />
      <CustomerBalanceModal open={customerBalanceOpen} onClose={() => setCustomerBalanceOpen(false)} onAddToCart={addAccountPaymentToCart} />
      <ShelfLocationLookupModal open={shelfLookupOpen} onClose={() => setShelfLookupOpen(false)} />
      <TillNoticeModal
        open={!!tillNoticeCustomer}
        customer={tillNoticeCustomer}
        onClose={() => setTillNoticeCustomer(null)}
      />

      {/* Active Customer Picker (Sales screen) — pick the customer up-front
          so their level discount appears on each cart line immediately. */}
      <Dialog open={customerPickerOpen} onOpenChange={setCustomerPickerOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-700" />
              Select Customer for this Sale
            </DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Search by name, account #, phone or email…"
            value={customerPickerQuery}
            onChange={(e) => setCustomerPickerQuery(e.target.value)}
            className="mb-2"
            data-testid="customer-picker-search-input"
          />
          <div className="border rounded overflow-y-auto flex-1 bg-white" data-testid="customer-picker-list">
            {customersLoading && (
              <div className="p-4 text-center text-sm text-slate-500">Loading customers…</div>
            )}
            {!customersLoading && filteredPickerCustomers.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-500">No customers match that search.</div>
            )}
            {filteredPickerCustomers.map((c) => {
              const lvl = CUSTOMER_LEVEL_DISCOUNTS[c.customer_level] || 0;
              const extra = Number(c.discount_percent) || 0;
              const pct = lvl + extra;
              return (
                <div
                  key={c.id}
                  onClick={() => handlePickCustomer(c)}
                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between"
                  data-testid={`customer-picker-row-${c.id}`}
                >
                  <div className="flex flex-col leading-tight">
                    <span className="font-semibold text-slate-900">
                      {c.name || c.business_name || "(unnamed)"}
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono">
                      {c.account_number ? `#${c.account_number} · ` : ""}
                      {c.customer_level || "Standard"}
                      {c.phone ? ` · ${c.phone}` : ""}
                    </span>
                  </div>
                  {pct > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                      {pct}% OFF
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setCustomerPickerOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan Dialog */}
      <LoanDialog
        open={loanOpen} onOpenChange={setLoanOpen}
        loanAmount={loanAmount} setLoanAmount={setLoanAmount}
        loanInterestRate={loanInterestRate} setLoanInterestRate={setLoanInterestRate}
        loanFromTill={loanFromTill} setLoanFromTill={setLoanFromTill}
        onAddLoan={handleAddLoan}
      />

      {/* Cash Withdrawal Dialog */}
      <CashWithdrawalDialog
        open={cashWithdrawalOpen} onOpenChange={setCashWithdrawalOpen}
        withdrawalAmount={withdrawalAmount} setWithdrawalAmount={setWithdrawalAmount}
        withdrawalFeeRate={withdrawalFeeRate} setWithdrawalFeeRate={setWithdrawalFeeRate}
        withdrawalFromTill={withdrawalFromTill} setWithdrawalFromTill={setWithdrawalFromTill}
        onAddWithdrawal={handleAddWithdrawal}
      />

      {checkoutOpen && (
        <CheckoutModal
          open={checkoutOpen}
          onClose={() => { setCheckoutOpen(false); setVoucherBlocked(false); }}
          cart={cart}
          calculateTotals={calculateTotals}
          priceLevel={priceLevel}
          onComplete={handleSaleComplete}
          vatEnabled={vatEnabled}
          promotionDiscount={promotionDiscount}
          appliedPromotions={appliedPromotions}
          promoPluCodes={promoPluCodes}
          initialCustomer={activeCustomer}
          salesperson={activeSalesperson}
          onVoucherIneligible={() => setVoucherBlocked(true)}
        />
      )}

      <DirectSaleLineDialog product={directSaleProduct} onClose={() => { setDirectSaleProduct(null); focusSearchInput(); }} onAdd={handleAddDirectLine} />

      {/* Weight Entry Popup */}
      <WeightDialog
        open={weightPopupOpen}
        onClose={() => { setWeightPopupOpen(false); setPendingWeightProduct(null); setWeightInput(""); }}
        pendingWeightProduct={pendingWeightProduct}
        getProductPrice={getProductPrice}
        weightInput={weightInput} setWeightInput={setWeightInput}
        weightInputRef={weightInputRef}
        onConfirm={confirmWeightAndAdd}
        focusSearchInput={focusSearchInput}
      />

      {/* Discount Entry Popup */}
      <DiscountDialog
        open={discountOpen}
        onClose={() => { setDiscountOpen(false); setDiscountInput(""); }}
        selectedCartItem={selectedCartItem} cart={cart}
        discountInput={discountInput} setDiscountInput={setDiscountInput}
        updateCartItemDiscount={updateCartItemDiscount}
        focusSearchInput={focusSearchInput}
      />

      {/* Fix Barcode Modal */}
      <FixBarcodeDialog
        open={fixBarcodeOpen} onOpenChange={setFixBarcodeOpen}
        fixBarcodePlu={fixBarcodePlu} setFixBarcodePlu={setFixBarcodePlu}
        fixBarcodeProduct={fixBarcodeProduct} setFixBarcodeProduct={setFixBarcodeProduct}
        fixBarcodeResults={fixBarcodeResults}
        fixBarcodeLoading={fixBarcodeLoading}
        fixBarcodeNewBarcode={fixBarcodeNewBarcode} setFixBarcodeNewBarcode={setFixBarcodeNewBarcode}
        fixBarcodeSaving={fixBarcodeSaving}
        onLookup={handleFixBarcodeLookup} onSave={handleFixBarcodeSave}
        onClose={() => { setFixBarcodeOpen(false); setFixBarcodePlu(""); setFixBarcodeNewBarcode(""); setFixBarcodeProduct(null); setFixBarcodeResults([]); focusSearchInput(); }}
        focusSearchInput={focusSearchInput}
      />

      {/* Enlarged Product Image Modal */}
      <Dialog open={!!enlargedImage} onOpenChange={(open) => !open && setEnlargedImage(null)}>
        <DialogContent className="max-w-2xl p-2">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg">{enlargedImage?.name}</DialogTitle>
            {enlargedImage?.plu && (
              <p className="text-sm text-slate-500">PLU: {enlargedImage.plu}</p>
            )}
          </DialogHeader>
          <div className="flex items-center justify-center bg-slate-100 rounded-lg overflow-hidden">
            {enlargedImage?.url && (
              <img 
                src={enlargedImage.url} 
                alt={enlargedImage?.name || "Product"} 
                className="max-w-full max-h-[60vh] object-contain"
                onError={(e) => {
                  e.target.src = '';
                  e.target.alt = 'Image failed to load';
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Service Amount Modal */}
      <VasServiceDialog
        serviceType={quickServiceOpen}
        onClose={resetVasState}
        amount={quickServiceAmount} setAmount={setQuickServiceAmount}
        vasNetwork={vasNetwork} setVasNetwork={setVasNetwork}
        vasMeterNumber={vasMeterNumber} setVasMeterNumber={setVasMeterNumber}
        vasProcessing={vasProcessing} vasResult={vasResult}
        onAdd={handleQuickServiceAdd}
      />

      {/* Hold Sales Modal */}
      <HeldSalesDialog
        open={holdSalesOpen} onOpenChange={setHoldSalesOpen}
        heldSales={heldSales}
        onRelease={handleReleaseSale} onDelete={handleDeleteHeldSale}
      />

      {/* Product Info Dialog for Cashier */}
      <Dialog open={productInfoOpen} onOpenChange={setProductInfoOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Info className="h-5 w-5 text-blue-500" />
              {productInfoData?.name || "Product Info"}
            </DialogTitle>
          </DialogHeader>
          {productInfoLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="ml-2 text-sm text-gray-500">Loading product info...</span>
            </div>
          ) : productInfoData ? (
            <div className="space-y-4">
              {productInfoData.description ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Description (English)</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{productInfoData.description}</p>
                </div>
              ) : (
                <div className="text-center py-4 space-y-3">
                  <p className="text-sm text-gray-400 italic">No product information available yet.</p>
                  <Button
                    onClick={fetchInfoFromWeb}
                    disabled={productInfoLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="fetch-info-web-btn"
                  >
                    {productInfoLoading ? (
                      <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Searching...</>
                    ) : (
                      <><Search className="h-4 w-4 mr-2" /> Fetch Info from Web</>
                    )}
                  </Button>
                </div>
              )}

              {productInfoData.description_northern_sotho ? (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-sm font-medium text-amber-700 mb-1">Sepedi / Northern Sotho</p>
                  <p className="text-sm text-amber-900 leading-relaxed">{productInfoData.description_northern_sotho}</p>
                </div>
              ) : productInfoData.description ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={translateToSepedi}
                  disabled={translating}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="translate-sepedi-btn"
                >
                  {translating ? (
                    <><div className="animate-spin h-3 w-3 border-2 border-amber-500 border-t-transparent rounded-full mr-2" /> Translating...</>
                  ) : (
                    "Translate to Sepedi"
                  )}
                </Button>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <PriceOverrideReasonDialog
        pending={pendingPriceOverride}
        onConfirm={confirmPriceOverride}
        onCancel={cancelPriceOverride}
      />
    </div>
  );
};

export default Sales;
